import axios from 'axios';
import { createStore } from "vuex"
import { initializeApp } from 'firebase/app';
import { getDatabase, ref, onValue, set, update, serverTimestamp, query, orderByChild, orderByKey, startAt, limitToFirst, push, get } from "firebase/database";
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  signOut,
  onAuthStateChanged
} from "firebase/auth";
import * as Sentry from "@sentry/vue";
import { getRating, rawScore } from "../assets/javascript/GetRating";
import { pushRoute } from '@/router/appRouter.js';
import { markSessionExpired } from '@/utils/sessionExpiredNotice.js';
import ErrorLogService from "../services/ErrorLogService.js";
import { markStalled, fetchWithTimeout } from '../utils/networkHealth.js';
import { placeholdersReadyToFinish, finalizePlaceholder } from '../assets/javascript/reconcilePlaceholder.js';

let autoReconcileRunning = false;
import { saveSnapshot, loadSnapshot } from "../utils/offlineStore.js";
import { maxUpdatedAt, reconstructFromDelta, diffLibraries, describeStaleEntry, launchPlan, isWriteRaceOnly, summarizeShadowReport } from "../assets/javascript/deltaSync.js";
import { enqueueWrite, listPendingWrites, removePendingWrite, updatePendingWrite } from "../utils/pendingWriteQueue.js";
import { setValueAtPath } from "../utils/statePath.js";
import { stampPlanForWrite, stampUpdatesForBatch } from "../assets/javascript/syncStamp.js";
import { emailToDatabaseKey, isQaAccountKey } from "../assets/javascript/databaseKey.js";
import { fetchAllHats, fetchMyHats, hatsForMember, fetchHat, fetchHatCard, readHatContents, toHatMovie, alreadyInHat, addMovieToHat, pickFromHat, commitDraw, isMovieHatAccessError } from "../assets/javascript/movieHat.js";
import {
  connectMovieHat as signIntoMovieHat,
  connectMovieHatWithToken as signIntoMovieHatWithToken,
  disconnectMovieHat as signOutOfMovieHat,
  watchMovieHatAuth as observeMovieHatAuth
} from "../assets/javascript/movieHatAuth.js";
import { isSignInDismissal, signInFailureSummary } from "../assets/javascript/movieHatSignIn.js";
import { buildSocialProfile, socialSettingsWithDefaults, countNewFriendUpdates, clubFetchesNeeded, feedBodyNeeded, EXTERNAL_FEED_MAX_AGE_MS } from "../assets/javascript/social.js";
import { buildMirrorFeed } from "../assets/javascript/mirrorFeed.js";
import { buildPushDigest } from "../assets/javascript/pushDigest.js";
import { appBadgeCount, badgeExtrasShown } from "../assets/javascript/appBadge.js";
import { pendingFriendRequests, readFriendRequestSnoozes } from "../assets/javascript/friendRequests.js";
import { readNoticeDismissals } from "../assets/javascript/homeNotices.js";
import { showtimesWaiting, readShowtimesSeenAt, SHOWTIMES_SEEN_KEY } from "../assets/javascript/showtimesUnread.js";
import { ticketsMark } from "../assets/javascript/showtimesTickets.js";
import { buildNewsletterProfile, buildPeopleList } from "../assets/javascript/newsletterProfile.js";
const PEOPLE_PUBLISH_EVERY_MS = 6 * 60 * 60 * 1000;
let peoplePublishedAt = 0;
import { postToNewsletter } from "../utils/newsletterRequest.js";
import { announceFriendRequest , closeNotificationsWhere } from "../utils/push.js";

// A rebuild takes about a minute end to end (measured at ~52s against the
// real APIs), so the poll has to outlast that with room to spare without
// hammering the database: 5s apart, up to two and a half minutes.
// The full Academy Awards dataset (~11k wins + nominations, every category,
// every ceremony) as a static file shipped with the app on S3/CloudFront.
// It used to come from the self-built film-awards-api on Railway; the data
// only changes once a year, so a server was pure cost. Yearly update: see
// "Academy Awards data" in CLAUDE.md.
export const ACADEMY_AWARDS_URL = '/data/academy-awards.json';
// The newest film year in that file. Part of the IndexedDB snapshot key, so a
// device that cached last year's dataset fetches the new one once this moves
// (scripts/build-oscars-year.mjs adds a ceremony; bump this with it).
export const ACADEMY_AWARDS_LATEST_YEAR = 2025;
const ACADEMY_SNAPSHOT = `allAcademyAwards-${ACADEMY_AWARDS_LATEST_YEAR}`;
const BEST_PICTURE_SNAPSHOT = `academyAwardWinners-${ACADEMY_AWARDS_LATEST_YEAR}`;
async function fetchAcademyAwardsDataset () {
  const response = await axios.get(ACADEMY_AWARDS_URL);
  return response.data || [];
}
const NEWSLETTER_REBUILD_POLL_MS = 5000;
const NEWSLETTER_REBUILD_TRIES = 30;
// The push sweep rewrites the theater board every 15 minutes.
const THEATER_BOARD_FRESH_MS = 15 * 60 * 1000;
import { pushPrefsWithDefaults } from "../assets/javascript/pushPrefs.js";
import { pendingUpdates, reconcilePending } from "../assets/javascript/recommendationStats.js";
import { toInterchange, profileFromFeed, buildInvite, parseInvite, buildConnectRequest, normalizeInboxRequests, buildDirectoryEntry, normalizeDirectory, findSubscription, dedupeExternalFriends, FEDERATED_APPS } from "../assets/javascript/interchange.js";
import { revisionUrlFor } from "../assets/javascript/feedRevision.js";
import { publishFeedV2 } from "../assets/javascript/filmClubSyncPublisher.js";
import { hexToken } from "../assets/javascript/filmClubSync.js";
import { refreshExternalFeed, feedFromSyncCache } from "../utils/filmClubSyncClient.js";
import { reportFeedFallback } from "../utils/syncFallbackReport.js";
import { planInbox, coalesceNotices, negotiationUpdate, noticeTargets, queueNotices, afterNoticeSend, buildNotice, friendSyncDue, isNegotiated, currentFeedId, newFeedId, INBOX_PAGE } from "../assets/javascript/clubNotices.js";
import { awardsByMovie } from "../assets/javascript/awardsShare.js";
import { awardNameWithThe } from "../assets/javascript/personalAwards.js";

const sortByVoteCount = (a, b) => {
  if (a.vote_count < b.vote_count) {
    return 1;
  }
  if (a.vote_count > b.vote_count) {
    return -1;
  }

  return 0;
}

const removeNaNAndUndefined = (obj) => {
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      if (typeof obj[key] === "object" && obj[key] !== null) {
        removeNaNAndUndefined(obj[key]);
      } else if (Number.isNaN(obj[key]) || obj[key] === undefined) {
        Sentry.captureMessage(`NaN or undefined value found in ${key}. The Object was ${JSON.stringify(obj)}`);
        delete obj[key];
      }
    }
  }
  return obj;
};

// Firebase RTDB's set() has no built-in timeout - while genuinely offline it
// resolves optimistically against the local cache (fine), but under a
// degraded/flaky connection (navigator.onLine still true, but the socket
// can't actually reach the server) it can stay pending INDEFINITELY, never
// resolving OR rejecting. Left unbounded, that hung promise would (a) block
// flushPendingWrites' one write forever and, worse, (b) keep
// isFlushingPendingWrites stuck true for the rest of the session, silently
// disabling every future flush attempt (a real data-loss bug - see the Jul
// 2026 bug report in CLAUDE.md's Offline Movie Rating section). Racing every
// write against a bounded timeout guarantees performDatabaseWrite always
// settles, so a stuck write becomes a recorded failure (retried on the next
// trigger) instead of a silent permanent stall.
// 8s, down from 15s (2026-09-23): the rating is durably queued BEFORE this
// attempt, so a shorter wait costs nothing but the user's patience - and on
// lie-fi (see networkHealth.js) a timed-out write is what tells the app the
// connection is dead, so the next save skips the wait entirely.
const DATABASE_WRITE_TIMEOUT_MS = 8000;

// Used to build absolute feed/inbox URLs for people on other apps.
const DATABASE_URL = 'https://movie-log-8c4d5-default-rtdb.firebaseio.com';

const isWriteTimeout = (error) => /timed out/.test(error?.message || '');
// A queued write that has failed this many flushes is stuck, not unlucky:
// that one gets a console.error (so Sentry hears about it), once.
export const QUEUED_WRITE_REPORT_AFTER = 5;

const withTimeout = (promise, ms, errorMessage) => {
  let timer;
  const timeout = new Promise((resolve, reject) => {
    timer = setTimeout(() => reject(new Error(errorMessage)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

// Shared write core behind both setDBValue (debounced, direct UI saves) and
// flushPendingWrites (queued offline/retry saves, which deliberately bypass
// that debounce - a queued entry is already a single, deliberately-deduped
// write, see pendingWriteQueue.js's enqueueWrite).
//
// Every write to a movieLog entry also records WHEN, and every deletion of one
// leaves a tombstone, so a future sync can ask for only what changed. The
// timestamp is assigned by Firebase's server, not this device's clock, so a
// device with a wrong clock can't hide a movie from itself. See syncStamp.js.
const performDatabaseWrite = async (context, dbEntry) => {
  try {
    const plan = stampPlanForWrite(dbEntry, serverTimestamp());
    const root = context.getters.databaseTopKey;

    const write = plan.kind === 'update'
      ? update(ref(db, root), removeNaNAndUndefined({ ...plan.updates }))
      : set(ref(db, `${root}/${plan.path}`), removeNaNAndUndefined(plan.value));

    await withTimeout(
      write,
      DATABASE_WRITE_TIMEOUT_MS,
      `Database write timed out after ${DATABASE_WRITE_TIMEOUT_MS}ms: ${dbEntry.path}`
    );
  } catch (error) {
    // A write that never came back is lie-fi's signature: flip the app to
    // its offline paths until something answers again (networkHealth.js).
    // It's a warning, not an error: captureConsole files every console.error
    // in Sentry, and a timeout is expected (iOS waking the PWA with a dead
    // socket, Sentry 2026-10-09). The write is already queued, and the flush
    // reports one that keeps failing (QUEUED_WRITE_REPORT_AFTER). A refusal
    // from the server (permission denied) is still an error.
    if (isWriteTimeout(error)) {
      console.warn('Database write timed out, left for the queue:', dbEntry.path);
      ErrorLogService.warn('Database write timed out:', dbEntry.path, error);
      markStalled();
    } else {
      console.error('Error setting database value:', error);
      ErrorLogService.error('Error setting database value:', dbEntry.path, error);
    }
    throw error;
  }
};

// Re-tops a freshly-arrived server snapshot with any local writes that are
// still in flight (committed locally, not yet server-confirmed), so a
// snapshot that legitimately predates one of them can't momentarily revert
// it in the UI. `rootKey` is which top-level branch this snapshot is
// ('settings' or 'movieLog'); only in-flight paths under that branch apply.
// See the trackInFlightWrite mutation for the bug this fixes.
const reapplyInFlightWrites = (state, rootKey, data) => {
  const paths = Object.keys(state.inFlightWrites || {});
  if (!paths.length) return data;

  let result = data;
  paths.forEach((path) => {
    const segments = path.split('/').filter(Boolean);
    if (segments[0] !== rootKey || segments.length < 2) return;
    result = setValueAtPath(result, segments.slice(1), state.inFlightWrites[path]);
  });
  return result;
};

// Firebase
const firebaseConfig = {
  apiKey: process.env.VUE_APP_GOOGLE_API_KEY,
    // cinemaroll.org, not movie-log-8c4d5.firebaseapp.com (2026-10-08): the sign-in popup lands on
  // /__/auth/handler, and served from another site iOS Safari's storage
  // partitioning could strip it of its session state ("missing initial
  // state"). CloudFront proxies /__/auth/* to the Firebase handler, so the
  // flow is first-party; the OAuth client lists this domain's handler.
  authDomain: "cinemaroll.org",
  databaseURL: "https://movie-log-8c4d5-default-rtdb.firebaseio.com",
  projectId: "movie-log-8c4d5",
  storageBucket: "movie-log-8c4d5.appspot.com",
  messagingSenderId: "84563192115",
  appId: "1:84563192115:web:121c681b37d284dcc93646",
  measurementId: "G-4K1Y42HFSL"
};

initializeApp(firebaseConfig);

// getAuth() MUST be called before getDatabase(), and at module load rather than
// lazily inside an action. The Realtime Database SDK picks up its auth-token
// provider from whatever is already registered on the app when it initialises;
// if Auth has never been instantiated it simply sends unauthenticated requests.
// That is invisible today (the database rules are open) but becomes a hard
// failure the moment those rules require `auth != null`.
const auth = getAuth();

const db = getDatabase();

ErrorLogService.setSignedInCheck(() => Boolean(auth.currentUser));

// A library or settings listener refused with nobody signed in is an expired
// session the sign-out already handles (verifyRestoredSession), so it's a
// warning — captureConsole files every console.error in Sentry (2026-10-11).
// Refused while signed in is a real rules problem and stays an error.
function reportRefusedListener (label, error) {
  if (auth.currentUser) {
    ErrorLogService.error(`${label} listener cancelled:`, error);
  } else {
    ErrorLogService.warn(`${label} listener cancelled with no sign-in:`, error);
  }
}
// The same split for a one-off read nobody else catches (Sentry, 2026-10-15:
// the launch push-settings read rejected unhandled on an expired session).
function reportRefusedRead (label, error) {
  if (auth.currentUser) console.error(`Failed to load ${label}:`, error);
  else console.warn(`Failed to load ${label} with no sign-in:`, error?.message);
}
let letterboxdLoadInFlight = null;
// One external friend's feed, v2 first. True when the feed answered (changed
// or not), false when it could not be read — a caller holding a pending
// notice keeps it then. Updates the friend's profile and error in the store.
async function syncOneExternalFriend (context, id, friend) {
  if (!friend?.feedUrl) return false;
  try {
    // v2 first (Brian's sync guide, 2026-10-06): two small head reads,
    // then nothing, a journal delta, or a paged bootstrap; the certified
    // snapshot is kept per friend across launches. Only when the head
    // cannot be trusted does the legacy body get fetched.
    const cached = context.state.externalFriendProfiles?.[id];
    const topKey = context.state.databaseTopKey;
    const syncKind = `externalSync:${id}`;
    const syncCache = topKey ? await loadSnapshot(topKey, syncKind).catch(() => null) : null;
    const v2 = await refreshExternalFeed({ feedUrl: friend.feedUrl, cache: syncCache });
    if (v2.status === 'revoked') {
      if (topKey) saveSnapshot(topKey, syncKind, null).catch(() => {});
      context.commit('setExternalFriendProfile', { id, profile: null });
      throw new Error('feed access was revoked');
    }
    if (v2.status === 'updated') {
      if (topKey) saveSnapshot(topKey, syncKind, v2.cache).catch(() => {});
      const profile = profileFromFeed(v2.feed, { fallbackName: friend.name });
      if (profile) { context.commit('setExternalFriendProfile', { id, profile }); return true; }
    }
    // No answer from the feed (bad signal, a timeout): keep what is
    // shown and what is saved, report nothing, and skip the legacy body.
    const answered = v2.status !== 'unreachable';
    const keepCached = v2.status === 'unchanged' || v2.status === 'unreachable';
    if (keepCached && cached) return answered;
    if (keepCached && syncCache?.meta) {
      // The snapshot is certified but this session has no profile yet (a cold start).
      const profile = profileFromFeed(feedFromSyncCache(syncCache), { fallbackName: friend.name });
      if (profile) { context.commit('setExternalFriendProfile', { id, profile }); return answered; }
    }
    if (v2.status === 'v1' && topKey && v2.cache) saveSnapshot(topKey, syncKind, v2.cache).catch(() => {});
    // A feed that used to validate and no longer does is an alarm, not a
    // quiet downgrade (Matt, 2026-10-06): a Sentry warning, once an hour.
    if (v2.status === 'v1') reportFeedFallback({ friendName: friend.name, feedUrl: friend.feedUrl, reason: v2.reason, established: Boolean(v2.cache?.establishedV2) });
    // Legacy body, with its revision preflight.
    const revisionUrl = cached?.revision ? revisionUrlFor(friend.feedUrl) : null;
    if (revisionUrl) {
      let headRevision = null;
      try {
        const head = await fetchWithTimeout(revisionUrl, { cache: 'no-store' });
        if (head.ok) headRevision = await head.json();
      } catch {
        // Fall through to the body.
      }
      if (!feedBodyNeeded({ cached, headRevision })) return true;
    }
    const response = await fetchWithTimeout(friend.feedUrl, { cache: 'no-store' });
    if (!response.ok) throw new Error(`feed responded ${response.status}`);
    const profile = profileFromFeed(await response.json(), { fallbackName: friend.name });
    if (!profile) throw new Error('unrecognised feed format');
    context.commit('setExternalFriendProfile', { id, profile });
    return true;
  } catch (error) {
    console.warn('[film-club] could not sync external friend', friend.name, error.message);
    context.commit('setExternalFriendError', { id, message: error.message });
    return false;
  }
}

// The body of sendClubNotices (one run at a time).
async function sendNoticesNow (context, revision) {
  const me = context.state.databaseTopKey;
  const secret = context.state.settings?.clubFeedKey;
  const feedId = currentFeedId(context.state.settings?.clubFeedId, secret);
  if (!me || !secret || !feedId) return;
  let outbox = (await loadSnapshot(me, 'clubNoticeOutbox').catch(() => null)) || {};
  if (revision) outbox = queueNotices(outbox, noticeTargets(context.state.settings?.externalFriends, feedId), revision);
  if (!Object.keys(outbox).length) return;
  for (const [id, entry] of Object.entries(outbox)) {
    // Same account, same capability, still a negotiated friend, same callback.
    if (context.state.databaseTopKey !== me || context.state.settings?.clubFeedKey !== secret) return;
    const target = noticeTargets(context.state.settings?.externalFriends, feedId).find((t) => t.id === id);
    if (!target) { outbox = afterNoticeSend(outbox, id, true); continue; }
    let ok = false;
    try {
      const response = await fetchWithTimeout(target.inboxUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildNotice({ feed: feedId, revision: entry.revision }))
      });
      ok = response.ok;
    } catch {
      // Counted as one attempt below.
    }
    outbox = afterNoticeSend(outbox, id, ok);
  }
  await saveSnapshot(me, 'clubNoticeOutbox', outbox).catch(() => {});
}

function saveExternalFriendsSnapshot (context) {
  const topKey = context.state.databaseTopKey;
  if (!topKey) return;
  saveSnapshot(topKey, 'externalFriends', {
    profiles: context.state.externalFriendProfiles,
    syncedAt: context.state.externalFriendsSyncedAt,
    syncedAtById: context.state.externalFriendSyncedAt
  }).catch(() => {});
}

// The inbox listener's path, so a second screen asking for it attaches nothing.
let clubInboxWatching = null;
// Inbox snapshots are processed one at a time, in order.
let clubInboxProcessing = Promise.resolve();
// One notice-driven refresh per friend at a time; notices sent one batch at a time.
const noticeRefreshes = new Map();
let clubNoticeSending = Promise.resolve();

// Unsubscribers for the Film Club requests + friends listeners, so signing
// out can drop them and the next sign-in attaches fresh ones.
let socialListenerStops = [];

// Pending debounced profile publish (see scheduleSocialPublish).
let socialPublishTimer = null;
// The one ensurePersonalAwards read in progress, shared by every caller.
let personalAwardsInFlight = null;

// Resolves true once settings.personalAwards can be trusted: loaded from the
// node or the offline copy, or still riding in the settings node (no stamp
// yet). False when the node exists but could not be read and nothing is
// cached — a publish then must wait rather than go out with no awards.
async function loadPersonalAwards (context) {
  const topKey = context.state.databaseTopKey;
  if (!topKey) return true;
  if (context.state.personalAwards == null) {
    const cached = await loadSnapshot(topKey, 'personalAwards').catch(() => null);
    if (cached?.data) context.commit('setPersonalAwards', cached);
  }
  try {
    const stamp = (await get(ref(db, `${topKey}/personalAwardsMeta/updatedAt`))).val();
    if (!stamp) return true;
    if (stamp === context.state.personalAwardsStamp && context.state.personalAwards) return true;
    const data = (await get(ref(db, `${topKey}/personalAwards`))).val() || {};
    context.commit('setPersonalAwards', { data, updatedAt: stamp });
    saveSnapshot(topKey, 'personalAwards', { data, updatedAt: stamp }).catch(() => {});
    return true;
  } catch (error) {
    console.warn('personal awards: could not refresh', error?.message);
    return context.state.personalAwards != null;
  }
}

// Publishing shares my awards with the club. Until they have loaded, a
// publish would send none and wipe them from every friend's view (report
// 2026-10-09: "I can't see Seth's or Natalie's" awards) — so wait for them,
// and skip the publish if they can't be had.
async function personalAwardsReadyToPublish (context) {
  if (context.state.personalAwards != null) return true;
  return context.dispatch('ensurePersonalAwards');
}
// Pending debounced Magic Mirror feed publish (see scheduleMirrorPublish).
let mirrorPublishTimer = null;
// The one ensureMovieHatContents read in progress, shared by every caller.
let movieHatContentsInFlight = null;
// How long a complete read of the hats' contents stands (see ensureMovieHatContents).
export const MOVIE_HAT_CONTENTS_MAX_AGE_MS = 6 * 60 * 60 * 1000;

// Records that the profile was just published, so Home's every-six-hours
// backstop doesn't immediately repeat a ~100KB write. Every path that publishes
// off the debounce timer goes through here, or the backstop and the timer
// disagree about when the last write happened.
const stampSocialPublish = () => {
  try {
    localStorage.setItem('cinemaRoll.social.lastPublish', String(Date.now()));
  } catch {
    // Private mode / storage full: the publish itself still happened.
  }
};

// Offline fallback / warm-launch source: Firebase RTDB's web SDK has no disk
// persistence (unlike Firestore), so the onValue socket in initializeDB never
// fires without a live connection and dbLoaded would hang forever. The
// last-synced IndexedDB snapshot races it: whichever settles the UI first,
// the onValue callback (which always fires once connected, cache hit or
// miss) is the source of truth and overwrites/re-persists on arrival — the
// dbLoaded guard just stops a slower cache read from clobbering
// already-arrived live data. One read per launch, shared by that race AND
// the delta-sync shadow check, which needs the snapshot as it stood BEFORE
// this launch's live data re-persists it (an IndexedDB read transaction
// opened here sees the pre-overwrite value regardless of when it resolves).
//
// Started BEFORE initializeDB awaits the auth restore — the snapshot is this
// device's own copy and needs no token — so a warm launch paints the library
// as soon as the slower of the two settles, not the sum. Cached at module
// scope so the router guard re-dispatching initializeDB during that wait
// can't fan out into duplicate multi-megabyte reads.
let snapshotReadInFlight = null; // { key, promise }

function listenersLiveFor (context, key) {
  return context.state.dbListenersAttachedFor === key && !context.state.dbReadDenied;
}

function startSnapshotRead (context, key) {
  if (snapshotReadInFlight && snapshotReadInFlight.key === key && !listenersLiveFor(context, key)) {
    return snapshotReadInFlight.promise;
  }
  const promise = loadSnapshot(key, 'movieLog').catch(() => null);
  // The settings snapshot rides along, and lands BEFORE dbLoaded. It used
  // to be read only after the auth restore, ~1.2s later at phone speed, so
  // Home painted once without the rating-curve anchors and then rebuilt
  // every score and card when settings arrived: two ~0.9s renders where
  // one will do (2026-09-23 speed sweep, relaunch phases).
  const settingsPromise = loadSnapshot(key, 'settings').catch(() => null);
  snapshotReadInFlight = { key, promise };

  promise.then(async (cached) => {
    // The account changed while this read was in flight: whoever started
    // the read for the new key applies that one instead.
    if (context.getters.databaseTopKey !== key) return null;
    const cachedSettings = await settingsPromise;
    if (cachedSettings && !context.state.settingsLoaded && context.getters.databaseTopKey === key) {
      context.commit('setSettings', cachedSettings);
      context.commit('setSettingsLoaded', true);
      await context.dispatch('replayPendingWrites', 'settings');
    }
    if (cached && !context.state.dbLoaded) {
      context.commit('setMovieLog', cached);
    }
    // Re-top the snapshot with anything still durably queued — the
    // snapshot only mirrors the last SERVER state, so without this a
    // movie rated offline vanished from view on relaunch (bug
    // report). Also covers a snapshot-less first session: the queue
    // entry alone is enough to show the rating.
    if (!context.state.dbLoaded) {
      await context.dispatch('replayPendingWrites', 'movieLog');
    }
    if (!context.state.dbLoaded && (cached || Object.keys(context.state.movieLog).length)) {
      context.commit('setDbLoaded', true);
    }
    return null;
  }).catch(() => {});

  return promise;
}

// Firebase restores a persisted session asynchronously after page load. The
// router guard, meanwhile, decides you're logged in synchronously from
// localStorage — so without this, database listeners can be attached before the
// auth token exists. Anything that reads user data waits on this promise.
//
// Resolves with the restored user, or null if there is no live session.
let resolveAuthReady;
export const authReady = new Promise((resolve) => {
  resolveAuthReady = resolve;
});

onAuthStateChanged(auth, (user) => {
  // Only the FIRST callback settles the promise; later ones (sign-in, sign-out)
  // are ordinary state changes, and a promise can't be re-resolved anyway.
  resolveAuthReady(user);
});

export default createStore({
  state: {
    movieLog: {},
    settings: {},
    weights: [ // These values should all add up to 10 except that "stickiness" gets divided by 2 first
      { name: "love", weight: 2.8 },
      { name: "overall", weight: 2 },
      { name: "story", weight: 1.25 },
      { name: "direction", weight: 1.1 },
      { name: "imagery", weight: 0.9 },
      { name: "stickiness", weight: 1.9 },
      { name: "performance", weight: 0.7 },
      { name: "soundtrack", weight: 0.3 },
    ],
    academyAwardWinners: {},
    // The FULL raw Academy Awards dataset (every category, every year, wins
    // AND nominations — ~11k records from the same self-built film-awards-api
    // service `academyAwardWinners` above already uses, just the bare
    // `/awards` endpoint with no `category` filter, "let's just go ahead and
    // pull it down and store it locally so we can use it wherever we want").
    // Separate from `academyAwardWinners` (Best-Picture-only, TMDB-enriched
    // for the existing Home.vue "Best Picture" quick-link feature) rather
    // than replacing it — this is the raw, unenriched, ALL-category dataset
    // other features (e.g. the Connections game's awards category) can
    // synchronously filter/match against without a per-movie network call.
    allAcademyAwards: [],
    userEmail: null,
    // The name the sign-in provider knows you by ("Matt Grosso"), as opposed
    // to the address you signed in with. Bug report, 2026-08-20: "It would be
    // nice if we could use someone's real name when we mention them in film
    // club" — every Film Club mention used to fall back to the email's local
    // part ("mattgrosso"), because this was thrown away at login. Restored
    // from localStorage on boot so a mention isn't wrong for the moment
    // before Firebase settles. Only ever a DEFAULT: an explicitly-set
    // settings/social/displayName still wins (see socialSettingsWithDefaults).
    userRealName: window.localStorage.getItem('userRealName') || null,
    databaseTopKey: null,
    newEntrySearchResults: [],
    movieToRate: {},
    // Money sorts and captions expressed in BASE_YEAR dollars (inflation.js).
    // localStorage rather than the database: it's a way of LOOKING at the
    // library, like sort order, not a fact about it — and it should not cost
    // a write or wait on a sync to take effect.
    moneyInTodaysDollars: window.localStorage.getItem('moneyInTodaysDollars') === 'true',
    // { dbKey, index } while RateMovie is amending an existing viewing,
    // otherwise null — see setRatingToEdit.
    ratingToEdit: null,
    DBSearchValue: null,
    DBSortValue: null,
    DBSortOrder: null,
    showHeader: true,
    // The "Cinema Roll" title overlay on the header banner - separate from
    // showHeader (which hides the whole header) since a game with its own
    // custom banner graphic may want the banner visible but its own
    // branding baked into the image instead of the usual overlay.
    hideHeaderLogo: false,
    goHome: false,
    // A small summary of Home.vue's LIVE filter/result state, published by
    // Home itself so in-app bug reports can actually show what was on
    // screen (see bugReports.js). Purely diagnostic - nothing reads it for
    // behaviour, and it is never persisted.
    homePageLiveState: null,
    devModeTopKey: 'testing-database',
    // devModeTopKey: 'natalierosegrosso-gmail-com',
    // devModeTopKey: 'carrieseltzer-gmail-com',
    // devModeTopKey: 'hopper-seth-gmail-com',
    // devModeTopKey: 'brianpatrick1-gmail-com',
    // Read once at store creation, then kept in reactive state — see the
    // `devMode` getter below for why it can't just re-read localStorage.
    devMode: localStorage.getItem('devMode') === 'true',
    dbLoaded: false,
    // True when the live movieLog/settings listener was CANCELLED by the
    // server (permission denied under the locked-down rules — wrong account,
    // dead session, or dev-mode pointing at a database this account can't
    // read). Drives LibraryAccessBanner's "your movies aren't gone"
    // guidance; cleared the moment a listener successfully delivers data.
    dbReadDenied: false,
    // Which account key the live movieLog/settings listeners are attached
    // for — the explicit "attach exactly once" guard. This replaced a
    // does-state-have-data check that a game screen's play-counter write
    // could fool by landing in settings before initializeDB ran (bug: the
    // Insights awards pane vanished because the settings listener never
    // attached and personalAwards never loaded).
    dbListenersAttachedFor: null,
    // True once REAL settings have arrived (snapshot or live) — not just
    // local write scraps. Gates the game recorders' read-modify-writes.
    settingsLoaded: false,
    // Latest delta-sync shadow-mode comparison (phase 1) — see
    // runDeltaShadowCheck. Null until a launch has a baseline to compare.
    deltaShadowReport: null,
    // Social layer (2026-08-15). Live inbox + friend edges via listeners;
    // directory and friend profiles fetched on demand. Shapes and the
    // publish-don't-peek design live in src/assets/javascript/social.js.
    socialRequests: {},
    // The requests listener has answered once — Home waits on it before
    // revealing a notice (homeNotices.js, settle then hold).
    socialRequestsLoaded: false,
    socialEdges: {},
    socialDirectory: {},
    // Letterboxd (2026-09-29): the shared per-film public stats the sweep
    // fills (letterboxdFilms/<tmdbId>) and my own synced diary entries
    // (<topKey>/letterboxd/reviews). Loaded once per session on demand
    // (ensureLetterboxdData) — Insights, Home's chips and sort, the games and
    // the club read them; MovieDetail reads its one film live.
    letterboxdFilms: null,
    letterboxdReviews: null,
    letterboxdLoadedAt: 0,
    socialFriendProfiles: {},
    // When fetchFriendProfiles last STARTED. Profiles are one-shot reads, so
    // this is the only measure of how old they are (see clubFetchesNeeded).
    socialProfilesFetchedAt: 0,
    // Hats this account belongs to, from the one-off Movie Hat lookup.
    availableMovieHats: [],
    // TMDB ids of everything currently WAITING in a linked hat, so a "send
    // to hat" button can hide itself for a movie that is already in one
    // ("it would be nice if the hat icon button only appeared on movies
    // that aren't already in one of my hats", 2026-08-17). Drawn movies are
    // deliberately not counted: they have left the hat and are fair game
    // to put back in.
    movieHatMovieIds: {},
    movieHatContentsAt: 0,
    // Per hat, the ETag of the last movies read and the ids it held, so the
    // next read is conditional (audit, 2026-10-06). Kept across launches.
    movieHatContentsCache: {},
    // Per hat, the ETag and card of the last whole-hat read for the summaries.
    movieHatSummaryCache: {},
    // False when the last read missed a hat, so the ids above may be short.
    movieHatContentsComplete: true,
    // Per-hat cards for the watchlist's draw section.
    movieHatSummaries: [],
    // The Google address signed into Movie Hat's project, if any.
    movieHatEmail: null,
    // Why the linked hats couldn't be read, when they couldn't:
    // { reason: 'not-connected' | 'token-failed' | 'denied', email }.
    // Null when the last read worked. Movie Hat's rules refuse anonymous
    // reads outright, so without this the whole section just renders empty
    // or "couldn't load" and nothing anywhere says to sign back in.
    movieHatAccessError: null,
    // The last sign-in or hat-lookup failure, for bug reports only. A report
    // that just said "Cannot sign into Movie Hat" (2026-10-01) carried nothing
    // that could say why.
    movieHatLastFailure: null,
    // Friends on other apps (Movie Log), translated into the same profile
    // shape as native friends. Held in memory; the subscription itself
    // lives in settings/externalFriends.
    externalFriendProfiles: {},
    // Personal awards (audit, 2026-10-06): 479 KB of the 541 KB settings
    // node, downloaded on EVERY launch by the settings listener. They now
    // live at `<topKey>/personalAwards` with a stamp at
    // `<topKey>/personalAwardsMeta/updatedAt`; the stamp is read first (a few
    // bytes) and the awards only when it moved, otherwise from the offline
    // snapshot. In memory they still appear as `state.settings.personalAwards`
    // so every reader is unchanged; this is the authoritative copy.
    personalAwards: null,
    personalAwardsStamp: null,
    // True until the awards are known (the offline copy, the node, or — before
    // the migration — the settings node). The settings arrive first, and in
    // that gap every year looks untouched: Home flashed "a year is ready" and
    // saved the wrong year as today's pick (report 2026-10-11).
    personalAwardsPending: true,
    // When syncExternalFriends last ran to completion (ms); restored from the
    // offline snapshot with the profiles, so a cold start can stay quiet.
    externalFriendsSyncedAt: 0,
    // Per friend (ms), so a friend who sends change notices can be on the
    // hourly backstop while the rest keep five minutes. Falls back to the
    // restored externalFriendsSyncedAt.
    externalFriendSyncedAt: {},
    externalFriendErrors: {},
    // Connect requests from people on other apps (see clubInbox rules).
    clubInboxRequests: {},
    // Users of other apps, fetched from their public directories.
    federatedDirectory: [],
    federatedDirectoryLoading: false,
    socialAttachedFor: null,
    // Push notifications (see src/utils/push.js + pushPrefs.js). `pushPrefs`
    // is the stored `{topKey}/push/prefs` node (null until loaded);
    // `pushSubscribed` is whether ANY device holds a subscription — the gate
    // for bothering to publish the digest at all.
    pushPrefs: null,
    pushSubscribed: false,
    // The weekly newsletter (see aws-lambda/newsletter.js). The issue is
    // BUILT by the Lambda and only read here, so these three are the whole of
    // the client's state: what it says, whether they want it, and whether the
    // read has come back yet — `newsletterLoaded` exists so an empty issue and
    // an unread one can be told apart on screen.
    newsletterIssue: null,
    // The theater board the push sweep publishes (aws-lambda/push-notify.js):
    // every followed theater's upcoming listings, in pecking order.
    theaterBoard: null,
    // When loadTheaterBoard last committed (0 = never). The icon badge's
    // Showtimes count re-reads the board when this is older than a sweep.
    theaterBoardLoadedAt: 0,
    // { [theaterKey]: { [slug]: dismissedAt } } - films Matt has waved off the
    // Showtimes screen. Pruned to what is still on the board when it loads.
    theaterDismissed: {},
    // { [theaterKey]: { [slug]: { remindAt, sentAt?, ... } } } - films Matt swiped
    // left on; the sweep sends each and stamps sentAt.
    theaterReminders: {},
    // When this device last opened the Showtimes screen (localStorage,
    // stamped by ShowtimesScreen). The badge counts only films found since.
    showtimesSeenAt: readShowtimesSeenAt(),
    // { zip, place, theaters: [{ key, name }] } - the theaters this account
    // follows, best first (ShowtimesSetup.vue). null until loaded; absent
    // for most accounts, and for Matt means "the sweep's own list".
    theaterFollow: null,
    newsletterPrefs: null,
    newsletterLoaded: false,
    // When the user last opened the Film Club — drives the rainbow chip's
    // new-updates badge. Mirrored to localStorage so it survives reloads.
    filmClubLastSeen: Number(localStorage.getItem('cinemaRoll.filmClub.lastSeen') || 0),
    filteredResults: [],
    // Header banner: Home resolves bannerUrl on arrival based on bannerRequest
    // (set by MovieDetail/RateMovie/search links). See Header.vue + Home.resolveBanner.
    bannerUrl: null,
    bannerRequest: null, // { type: 'movie', movieId } | { type: 'fromResults' } | null
    // Home page navigation state preservation
    homePageScrollPosition: 0,
    homePageSearchChips: [],
    homePageSearchValue: '',
    homePageNumberOfResults: 25,
    homePageNavigationIntent: null, // 'close' | 'search' | null
    homePageSortValue: null, // preserve sort value for 'close' navigation
    homePageSortOrder: null, // preserve sort order for 'close' navigation
    homePagePromoteGroup: null, // group key to promote to top when navigating from a clicked value
    // Simple save debouncing
    lastSavePath: null,
    lastSaveTime: 0,
    // Offline rating support: isOnline drives which entry points/paths are
    // reachable (see AddRating.js, RateMovie.vue, Home.vue), and is flipped
    // by App.vue's 'online'/'offline' listeners. isFlushingPendingWrites
    // guards flushPendingWrites against overlapping passes when its several
    // triggers (see App.vue) fire close together. pendingReconciliations
    // mirrors the pendingWriteQueue's unreconciled placeholder entries, kept
    // in Vuex so Home.vue can reactively show a "needs a match" banner.
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    // True while the connection LOOKS up (navigator.onLine) but nothing is
    // answering - "lie-fi". Set by networkHealth.js when a request times
    // out; cleared when one succeeds, when a probe gets through, or when the
    // browser fires a fresh 'online' event. While true, isOnline is false so
    // every offline path in the app engages. Bug report 2026-09-23.
    networkStalled: false,
    // A screen is on its way (router guard); RouteProgress shows the bar.
    routePending: false,
    // { text, at } for SavedFlash's "Saved" pill; `at` makes repeats fire.
    savedFlash: null,
    // { [dbPath]: value } for writes committed locally but not yet confirmed
    // by the server — see the trackInFlightWrite mutation for why.
    inFlightWrites: {},
    isFlushingPendingWrites: false,
    pendingReconciliations: [],
    // Flipped by registerServiceWorker.js's `updated` hook once a new
    // version has installed and activated in the background. Deliberately
    // does NOT auto-reload (see UpdateAvailableBanner.vue's own comment for
    // the bug report this fixed) - an unconditional window.location.reload()
    // there used to yank the page out from under whatever the user was
    // doing (e.g. mid-way through the box office backfill button).
    updateAvailable: false,
    // The deployed bundle name the check saw (`js/app.<hash>.js`), so the
    // reload path can tell a second attempt at the SAME update from a new
    // one (appUpdate.js).
    updateTargetBundle: null,
    // App.vue is applying the update on its own right now, or tried and the
    // connection couldn't carry it; the banner shows these instead of a
    // Refresh button (UpdateAvailableBanner.vue).
    updateApplying: false,
    updateDeferred: false,
    // Bumped when the service worker reports a new worker; App.vue watches
    // it and runs the bundle comparison (registerServiceWorker.js).
    updateCheckRequests: 0,
  },
  getters: {
    allMediaAsArray: (state) => {
      if (!state.dbLoaded) {
        return [];
      }

      return Object.keys(state.movieLog).map((key) => {
        const movie = state.movieLog[key];
        movie.dbKey = key;
        return movie;
      });
    },
    allMoviesAsArray: (state) => {
      return Object.keys(state.movieLog).map((key) => {
        return state.movieLog[key];
      })
    },
    allMediaSortedByRating: (state, getters) => {
      // A COPY, decorated once. This used to sort allMediaAsArray in place
      // (quietly reordering another getter's cached array for everyone)
      // and re-score both sides of every comparison - ~170ms of each
      // return to Home at desktop speed (2026-09-23 speed sweep).
      return getters.allMediaAsArray
        .map((media) => ({ media, score: rawScore(media) }))
        .sort((a, b) => b.score - a.score)
        .map((d) => d.media);
    },
    // dbKey -> 1-based overall rank, so a grid of 120 cards asks a Map
    // instead of each running findIndex over the whole library.
    overallRankByDbKey: (state, getters) => {
      const ranks = new Map();
      getters.allMediaSortedByRating.forEach((media, index) => ranks.set(media.dbKey, index + 1));
      return ranks;
    },
    allMediaRatingsArray: (state, getters) => {
      return getters.allMediaAsArray.map(rawScore);
    },
    databaseTopKey (state, getters) {
      return getters.devMode ? state.devModeTopKey : state.databaseTopKey;
    },
    // Social is keyed by the REAL signed-in account, never the dev-mode
    // sandbox — the database rules only allow writes under the key that
    // matches the auth token's email.
    socialUserKey (state) {
      return state.databaseTopKey;
    },
    socialSettings (state) {
      // Sharing is ON by default — the friend handshake is the real consent
      // gate (rules make unfriended profiles unreadable). Explicit false in
      // settings.social is the opt-out. See socialSettingsWithDefaults.
      return socialSettingsWithDefaults(state.settings?.social, state.userEmail, state.userRealName);
    },
    // Friendship = BOTH edges exist. My friends are my outgoing edges that
    // the other side has reciprocated.
    //
    // QA accounts are dropped here, at the single point everything else reads
    // through — friends list, profiles, the club summary, the update badge,
    // and the profile fetch. The database edges stay mutual on BOTH sides,
    // because the rules require that to let the tester read a profile at all;
    // the one-way-ness is this filter (Matt, 2026-08-16: "make it so that the
    // testing account is friends with me, but that I just don't see them in
    // my friend's list... they can see me, but I can't see them").
    //
    // Note this filters by the FRIEND's key, so it hides the tester from a
    // real person without hiding real people from the tester.
    socialFriendKeys (state, getters) {
      const me = getters.socialUserKey;
      if (!me) return [];
      const myEdges = state.socialEdges?.[me] || {};
      return Object.keys(myEdges)
        .filter((key) => state.socialEdges?.[key]?.[me])
        .filter((key) => !isQaAccountKey(key));
    },
    // Requests I've sent that the other side hasn't accepted yet: my
    // outgoing edges with no reciprocal edge.
    // New friend ratings since the Film Club was last opened — only
    // mutual friends' profiles count (others aren't readable anyway).
    filmClubNewUpdateCount (state, getters) {
      const profiles = {};
      getters.socialFriendKeys.forEach((key) => {
        if (state.socialFriendProfiles?.[key]) profiles[key] = state.socialFriendProfiles[key];
      });
      return countNewFriendUpdates(profiles, state.filmClubLastSeen);
    },
    // Showtimes films on the icon badge: every film found since this device
    // last opened the Showtimes screen and still waiting there (report
    // 2026-11-04: counting the whole board kept the icon at 50+), while the "Showtimes waiting" switch is on (off by
    // default, so only `true` counts). ONE number for the badge
    // (refreshAppBadge), Home's Showtimes card and the Watchlist's - bug
    // report 2026-10-06: "there's nothing on the home screen that guides me
    // to where I need to go" to clear the badge.
    // Friend requests waiting for an answer — the icon badge counts these
    // (refreshAppBadge); Home's card applies the same rule.
    pendingFriendRequests (state, getters) {
      return pendingFriendRequests(state.socialRequests, state.socialEdges, getters.socialUserKey);
    },
    showtimesBadgeCount (state) {
      if (state.pushPrefs?.showtimes !== true) return 0;
      return showtimesWaiting({
        board: state.theaterBoard,
        dismissed: state.theaterDismissed || {},
        reminders: state.theaterReminders || {},
        seenAt: state.showtimesSeenAt || 0
      });
    },
    // One list for the whole club: native mutual friends and friends on
    // other apps, in the same shape, so nothing downstream needs to care
    // which app someone uses.
    crossAppDiscoveryEnabled (state) {
      return Boolean(state.settings?.crossAppDiscovery);
    },
    // Only what a person should decide on: never a change notice, and never
    // an existing friend's negotiation that is answered automatically (one
    // we can't prove they're entitled to stays, as an ordinary request).
    clubInboxRequests (state) {
      const friends = state.settings?.externalFriends || {};
      const plan = planInbox({ raw: state.clubInboxRequests, friends });
      const feedId = currentFeedId(state.settings?.clubFeedId, state.settings?.clubFeedKey);
      const shown = { ...plan.requests };
      plan.negotiations
        .filter(({ friendId, request }) => !negotiationUpdate({ friend: friends[friendId], request, feedId }).proven)
        .forEach(({ key, request }) => { shown[key] = request; });
      return normalizeInboxRequests(shown);
    },
    filmClubFriends (state, getters) {
      const native = getters.socialFriendKeys.map((key) => ({
        key,
        // Profile first, then the directory row, then — only if this person
        // has published neither — the database key. That middle step matters:
        // profiles are fetched lazily and are ~100KB each, so before they
        // land every friend was being MENTIONED BY DATABASE KEY, which is a
        // sanitized email address (bug report, 2026-08-20: "It would be nice
        // if we could use someone's real name when we mention them in film
        // club"). The directory row is already in memory and carries the same
        // name the profile will.
        name: state.socialFriendProfiles?.[key]?.name || state.socialDirectory?.[key]?.name || key,
        profile: state.socialFriendProfiles?.[key] || null,
        external: false
      }));
      // Deduped on the way out as well as on the way in, so a club that was
      // already doubled reads correctly straight away rather than waiting
      // for anyone to prune their settings.
      const external = Object.entries(dedupeExternalFriends(state.settings?.externalFriends)).map(([id, friend]) => ({
        key: id,
        name: state.externalFriendProfiles?.[id]?.name || friend?.name || 'A friend',
        profile: state.externalFriendProfiles?.[id] || null,
        external: true,
        source: state.externalFriendProfiles?.[id]?.source || 'external',
        error: state.externalFriendErrors?.[id] || null
      }));
      return [...native, ...external].sort((a, b) => a.name.localeCompare(b.name));
    },
    // Keyed profiles for everything that aggregates across the club
    // (summary, watchlist picks).
    filmClubProfiles (state, getters) {
      const out = {};
      getters.filmClubFriends.forEach((friend) => {
        if (friend.profile) out[friend.key] = friend.profile;
      });
      return out;
    },
    /** Hats this account has linked, as [{ title, dbKey }]. */
    linkedMovieHats (state) {
      const hats = state.settings?.movieHat?.hats;
      const list = Array.isArray(hats)
        ? hats.filter((hat) => hat && hat.title)
        // Firebase turns a sparse array back into an object map.
        : (hats && typeof hats === 'object'
          ? Object.values(hats).filter((hat) => hat && hat.title)
          : []);

      // Most recently used first: "the order of my hats should be based on
      // which hat [I] most recently added to, so they should get sorted by
      // how recently I used them" (2026-08-17). A hat never used yet keeps
      // its configured position behind the ones that have been.
      return [...list].sort((a, b) => (Number(b.lastUsedAt) || 0) - (Number(a.lastUsedAt) || 0));
    },
    socialPendingSentKeys (state, getters) {
      const me = getters.socialUserKey;
      if (!me) return [];
      const myEdges = state.socialEdges?.[me] || {};
      return Object.keys(myEdges).filter((key) => !state.socialEdges?.[key]?.[me]);
    },
    // Backed by state.devMode (set via the setDevMode mutation), not a direct
    // localStorage read. A Vuex getter is a Vue computed under the hood: with
    // no reactive dependency (a bare `localStorage.getItem(...)` read touches
    // no observed state), it only evaluates once and then caches that value
    // forever, so toggling localStorage directly — from another tab, devtools,
    // or a differently-reloaded page — would never be picked up without a
    // full app reload. Routing state.devMode through here keeps it reactive.
    devMode (state) {
      return state.devMode;
    },
    weight (state) {
      return (name) => {
        return state.weights.find((weight) => weight.name === name).weight;
      }
    },
  },
  mutations: {
    setMovieLog (state, value) {
      state.movieLog = Object.freeze(value);
    },
    setSettings (state, value) {
      // The awards live apart (see state.personalAwards); the settings node
      // no longer carries them once migrated, so they are re-attached here.
      state.settings = state.personalAwards && value && !value.personalAwards
        ? { ...value, personalAwards: state.personalAwards }
        : value;
    },
    setPersonalAwards (state, { data, updatedAt }) {
      state.personalAwards = data || {};
      state.personalAwardsStamp = updatedAt ?? null;
      state.personalAwardsPending = false;
      state.settings = { ...(state.settings || {}), personalAwards: state.personalAwards };
    },
    setPersonalAwardsPending (state, value) {
      state.personalAwardsPending = value;
    },
    setPersonalAwardsStamp (state, updatedAt) {
      state.personalAwardsStamp = updatedAt ?? null;
    },
    setAcademyAwardWinners (state, value) {
      state.academyAwardWinners = value
    },
    setSocialRequests (state, value) {
      state.socialRequests = value || {};
      state.socialRequestsLoaded = true;
    },
    setSocialEdges (state, value) {
      state.socialEdges = value || {};
    },
    setAvailableMovieHats (state, value) {
      state.availableMovieHats = value || [];
    },
    setMovieHatContents (state, { ids, at, complete = true, cache } = {}) {
      state.movieHatMovieIds = ids || {};
      state.movieHatContentsAt = at || 0;
      state.movieHatContentsComplete = complete !== false;
      if (cache) state.movieHatContentsCache = cache;
    },
    setMovieHatSummaryCache (state, value) {
      state.movieHatSummaryCache = value || {};
    },
    // Sent successfully: hide those buttons now rather than after the next
    // refresh of the cache.
    noteMoviesInHat (state, ids) {
      (ids || []).forEach((id) => {
        if (id != null) state.movieHatMovieIds[id] = true;
      });
    },
    setMovieHatUser (state, user) {
      state.movieHatEmail = user?.email || null;
      // Connecting (or disconnecting) makes any previous refusal stale — the
      // next read is the only thing that can say whether it still applies.
      state.movieHatAccessError = null;
    },
    setMovieHatLastFailure (state, value) {
      state.movieHatLastFailure = value || null;
    },
    setMovieHatAccessError (state, value) {
      state.movieHatAccessError = value || null;
    },
    setMovieHatSummaries (state, value) {
      state.movieHatSummaries = value || [];
    },
    setSocialDirectory (state, value) {
      state.socialDirectory = value || {};
    },
    setFederatedDirectory (state, rows) {
      state.federatedDirectory = rows || [];
    },
    setFederatedDirectoryLoading (state, value) {
      state.federatedDirectoryLoading = Boolean(value);
    },
    setClubInboxRequests (state, value) {
      state.clubInboxRequests = value || {};
    },
    setExternalFriendProfile (state, { id, profile }) {
      const next = { ...state.externalFriendProfiles };
      if (profile) next[id] = profile; else delete next[id];
      state.externalFriendProfiles = next;
    },
    setExternalFriendsSyncedAt (state, at) {
      state.externalFriendsSyncedAt = Number(at) || 0;
    },
    markExternalFriendsSynced (state, { ids, at }) {
      const next = { ...state.externalFriendSyncedAt };
      (ids || []).forEach((id) => { next[id] = Number(at) || 0; });
      state.externalFriendSyncedAt = next;
    },
    restoreExternalFriends (state, { profiles, syncedAt, syncedAtById }) {
      // Only fill what the session hasn't fetched already.
      state.externalFriendProfiles = { ...(profiles || {}), ...state.externalFriendProfiles };
      if (!state.externalFriendsSyncedAt) state.externalFriendsSyncedAt = Number(syncedAt) || 0;
      state.externalFriendSyncedAt = { ...(syncedAtById || {}), ...state.externalFriendSyncedAt };
    },
    setExternalFriendError (state, { id, message }) {
      state.externalFriendErrors = { ...state.externalFriendErrors, [id]: message };
    },
    setSocialFriendProfile (state, { key, profile }) {
      state.socialFriendProfiles = { ...state.socialFriendProfiles, [key]: profile };
    },
    setLetterboxdData (state, { films, reviews, at }) {
      // Frozen like movieLog: thousands of small objects nobody edits.
      state.letterboxdFilms = Object.freeze(films || {});
      state.letterboxdReviews = Object.freeze(reviews || {});
      state.letterboxdLoadedAt = at;
    },
    setSocialProfilesFetchedAt (state, value) {
      state.socialProfilesFetchedAt = value;
    },
    setSocialAttachedFor (state, value) {
      state.socialAttachedFor = value;
    },
    setPushPrefs (state, value) {
      state.pushPrefs = value;
    },
    setPushSubscribed (state, value) {
      state.pushSubscribed = Boolean(value);
    },
    setNewsletterIssue (state, value) {
      state.newsletterIssue = value || null;
    },
    setTheaterBoard (state, value) {
      state.theaterBoard = value || null;
      state.theaterBoardLoadedAt = Date.now();
    },
    setShowtimesSeenAt (state, value) {
      state.showtimesSeenAt = Number(value) || 0;
      try { localStorage.setItem(SHOWTIMES_SEEN_KEY, String(state.showtimesSeenAt)); } catch { /* private mode */ }
    },
    setTheaterDismissed (state, value) {
      state.theaterDismissed = value && typeof value === 'object' ? value : {};
    },
    setTheaterFollow (state, value) {
      state.theaterFollow = value && typeof value === 'object' ? value : null;
    },
    setTheaterReminders (state, value) {
      state.theaterReminders = value && typeof value === 'object' ? value : {};
    },
    setNewsletterPrefs (state, value) {
      state.newsletterPrefs = value || null;
    },
    setNewsletterLoaded (state, value) {
      state.newsletterLoaded = Boolean(value);
    },
    markFilmClubSeen (state) {
      state.filmClubLastSeen = Date.now();
      localStorage.setItem('cinemaRoll.filmClub.lastSeen', String(state.filmClubLastSeen));
    },
    setAllAcademyAwards (state, value) {
      state.allAcademyAwards = value;
    },
    setUserEmail (state, value) {
      state.userEmail = value;
    },
    setUserRealName (state, value) {
      const name = (value || '').trim() || null;
      state.userRealName = name;
      if (name) {
        window.localStorage.setItem('userRealName', name);
      } else {
        window.localStorage.removeItem('userRealName');
      }
    },
    setDatabaseTopKey (state, value) {
      state.databaseTopKey = emailToDatabaseKey(value);
    },
    setNewEntrySearchResults (state, value) {
      const results = [...value];
      const sorted = results.sort(sortByVoteCount);

      state.newEntrySearchResults = sorted;
    },
    setMoneyInTodaysDollars (state, on) {
      state.moneyInTodaysDollars = Boolean(on);
      window.localStorage.setItem('moneyInTodaysDollars', String(state.moneyInTodaysDollars));
    },
    setMovieToRate (state, movie) {
      state.movieToRate = movie;
      // Clearing here, not at the call sites. Every route into RateMovie
      // commits this mutation, so a stale `ratingToEdit` left over from a
      // previous edit would turn the NEXT "Add New Rating" into an
      // overwrite of an old viewing — silent data loss. Making the reset
      // part of this mutation means no call site can forget it; the edit
      // path commits setRatingToEdit immediately after this one.
      state.ratingToEdit = null;
    },
    // { dbKey, index } — which existing rating RateMovie is amending, or
    // null for a brand-new one. Deliberately an index into the entry's own
    // `ratings` array (the same index deleteRating uses); `getAllRatings`
    // maps 1:1 over it, so what the accordion shows at row N is ratings[N].
    setRatingToEdit (state, target) {
      state.ratingToEdit = target;
    },
    // Applies a single movieLog write to local state immediately, regardless
    // of connectivity - the optimistic-commit half of offline rating support
    // (see AddRating.js). Without this, an offline write would only queue to
    // IndexedDB and wouldn't show up anywhere (grid, previous viewings, a
    // same-session re-edit) until the queue actually flushes. setMovieLog
    // freezes movieLog wholesale, so this rebuilds+refreezes a new object
    // rather than mutating the existing (frozen) one in place.
    setMovieLogEntry (state, { key, value }) {
      state.movieLog = Object.freeze({ ...state.movieLog, [key]: value });
    },
    // Batched counterpart to setMovieLogEntry - applies MANY entries in one
    // spread+freeze+reactivity-trigger instead of one per entry (bug fix,
    // Jul 2026: the box office backfill button called setMovieLogEntry once
    // per movie as each one completed - for a large library that's hundreds
    // of full movieLog copies + reactivity cascades in rapid succession,
    // severe enough to freeze/crash the tab on a real device. Batching
    // callers, e.g. backfillBoxOffice.js, cuts that down by the batch size).
    setMovieLogEntries (state, entries) {
      const updated = { ...state.movieLog };
      entries.forEach(({ key, value }) => { updated[key] = value; });
      state.movieLog = Object.freeze(updated);
    },
    // General-purpose counterpart to setMovieLogEntry/setMovieLogEntries for
    // any OTHER db path — settings/* (any depth: settings/lastTweak,
    // settings/personalAwards/2024, ...) and deeper movieLog/* paths that
    // aren't a plain full-entry replace. Added for the offline-support
    // extension to Stickiness/Tiebreak/Personal Awards (Jul 2026) — those
    // three write mostly to settings/*, which had NO local-optimistic-commit
    // mutation at all before this (unlike movieLog, which got one during the
    // original offline-rating work) - without it, an offline write to e.g.
    // settings/tieBreakTournament wouldn't show up anywhere until the queue
    // actually flushed. Reuses setMovieLogEntry's exact shape for the common
    // movieLog/<key> case (freeze + reactivity trigger) rather than routing
    // it through the generic recursive path, since that's already proven and
    // used by every existing movieLog writer.
    applyDbPathLocally (state, { path, value }) {
      const segments = (path || '').split('/').filter(Boolean);
      const [root, ...rest] = segments;
      if (!root || !rest.length) return;

      if (root === 'movieLog' && rest.length === 1) {
        state.movieLog = Object.freeze({ ...state.movieLog, [rest[0]]: value });
        return;
      }
      if (root === 'movieLog') {
        state.movieLog = Object.freeze(setValueAtPath(state.movieLog, rest, value));
        return;
      }
      if (root === 'personalAwards') {
        state.personalAwards = setValueAtPath(state.personalAwards || {}, rest, value);
        state.settings = { ...(state.settings || {}), personalAwards: state.personalAwards };
        return;
      }
      // Showtimes dismissals and reminders (2026-10-09): theaters/dismissed
      // and theaters/reminders, one theater/slug leaf at a time. A null is a
      // removal, so the key goes rather than sitting there as null.
      if (root === 'theaters' && (rest[0] === 'dismissed' || rest[0] === 'reminders') && rest.length === 3) {
        const field = rest[0] === 'dismissed' ? 'theaterDismissed' : 'theaterReminders';
        const [, theaterKey, slug] = rest;
        const forTheater = { ...((state[field] || {})[theaterKey] || {}) };
        if (value == null) delete forTheater[slug];
        else forTheater[slug] = value;
        state[field] = { ...(state[field] || {}), [theaterKey]: forTheater };
        return;
      }
      if (root === 'settings') {
        // The migration deletes settings/personalAwards; the awards themselves
        // live on in state.personalAwards, so that delete must not blank them.
        if (rest[0] === 'personalAwards' && state.personalAwards) return;
        state.settings = setValueAtPath(state.settings, rest, value);
      }
    },
    // Bug report (Jul 2026): "After I break a tie, I get the tie break
    // message again just for a second or two." A tiebreak fires several
    // writeDurably calls back to back (the tournament record, the movieLog
    // score adjustments, the settings/lastTweak quota stamp). Each one
    // commits locally first, but its Firebase set() was serialized BEHIND an
    // IndexedDB enqueue - so the first write reached the server while a
    // later one (lastTweak) hadn't been issued yet. The server's onValue
    // then fired with a snapshot that legitimately predated lastTweak, and
    // setSettings replaces settings WHOLESALE - clobbering the local commit
    // and flipping the quota check back to "due", which re-showed the
    // notice until the lastTweak write finally landed.
    //
    // These two mutations track a write from the moment it's committed
    // locally until the server confirms it, so an incoming snapshot can be
    // re-topped with anything still in flight (see reapplyInFlightWrites)
    // instead of momentarily reverting it.
    trackInFlightWrite (state, { path, value }) {
      state.inFlightWrites = { ...state.inFlightWrites, [path]: value };
    },
    untrackInFlightWrite (state, path) {
      const next = { ...state.inFlightWrites };
      delete next[path];
      state.inFlightWrites = next;
    },
    setRoutePending (state, value) {
      state.routePending = value;
    },
    flashSaved (state, text = 'Saved') {
      state.savedFlash = { text, at: Date.now() };
    },
    setIsOnline (state, value) {
      state.isOnline = value;
      // The browser's own 'online' event is a fresh signal: give the
      // connection another chance. The next timed-out request re-flags it.
      if (value) state.networkStalled = false;
    },
    setNetworkStalled (state, value) {
      state.networkStalled = value;
      state.isOnline = value ? false : (typeof navigator !== 'undefined' ? navigator.onLine : true);
    },
    setIsFlushingPendingWrites (state, value) {
      state.isFlushingPendingWrites = value;
    },
    setPendingReconciliations (state, value) {
      state.pendingReconciliations = value;
    },
    setUpdateAvailable (state, value) {
      state.updateAvailable = value;
    },
    setUpdateTargetBundle (state, value) {
      state.updateTargetBundle = value;
    },
    setUpdateApplying (state, value) {
      state.updateApplying = value;
    },
    setUpdateDeferred (state, value) {
      state.updateDeferred = value;
    },
    requestUpdateCheck (state) {
      state.updateCheckRequests = (state.updateCheckRequests || 0) + 1;
    },
    setDBSearchValue (state, value) {
      state.DBSearchValue = value;
    },
    setDBSortValue (state, value) {
      state.DBSortValue = value;
    },
    setDBSortOrder (state, value) {
      state.DBSortOrder = value;
    },
    setShowHeader (state, value) {
      state.showHeader = value;
    },
    setHideHeaderLogo (state, value) {
      state.hideHeaderLogo = value;
    },
    setGoHome (state, value) {
      state.goHome = value;
    },
    setHomePageLiveState (state, value) {
      state.homePageLiveState = value;
    },
    setDbLoaded (state, value) {
      state.dbLoaded = value;
    },
    setDbReadDenied (state, value) {
      state.dbReadDenied = value;
    },
    setDbListenersAttachedFor (state, value) {
      state.dbListenersAttachedFor = value;
    },
    setSettingsLoaded (state, value) {
      state.settingsLoaded = value;
    },
    setDeltaShadowReport (state, value) {
      state.deltaShadowReport = value;
    },
    // Persists to localStorage here too, so this is the one place that needs
    // to know devMode is backed by localStorage at all — callers just commit.
    setDevMode (state, value) {
      state.devMode = value;
      localStorage.setItem('devMode', value);
    },
    setFilteredResults (state, value) {
      state.filteredResults = value;
    },
    setBannerUrl (state, value) {
      state.bannerUrl = value;
    },
    setBannerRequest (state, value) {
      state.bannerRequest = value;
    },
    updateLastSave (state, { path, time }) {
      state.lastSavePath = path;
      state.lastSaveTime = time;
    },
    // Home page navigation state mutations
    setHomePageScrollPosition (state, value) {
      state.homePageScrollPosition = value;
    },
    setHomePageSearchChips (state, value) {
      state.homePageSearchChips = value;
    },
    setHomePageSearchValue (state, value) {
      state.homePageSearchValue = value;
    },
    setHomePageNumberOfResults (state, value) {
      state.homePageNumberOfResults = value;
    },
    setHomePageNavigationIntent (state, value) {
      state.homePageNavigationIntent = value;
    },
    setHomePageSortValue (state, value) {
      state.homePageSortValue = value;
    },
    setHomePageSortOrder (state, value) {
      state.homePageSortOrder = value;
    },
    setHomePagePromoteGroup (state, value) {
      state.homePagePromoteGroup = value;
    }
  },
  actions: {
    // Shared tail of EVERY sign-in method (Google, Apple, email/password).
    // A user's whole library is keyed by their email address, so this is the
    // one place that derives the key, persists it, and boots the database —
    // no provider gets to do that its own way.
    completeLogin (context, user) {
      const email = user?.email;

      if (!email) {
        // Every provider we enable returns an email (Apple's "Hide My Email"
        // returns a stable per-app relay address, which works fine as a key).
        // If one somehow doesn't, failing loudly is much safer than dropping
        // the user into an empty, wrongly-keyed database.
        ErrorLogService.error('Sign-in succeeded but returned no email address', user);
        throw new Error("Signed in, but no email address came back. Cinema Roll keys your library by email, so it can't continue.");
      }

      context.commit('setUserEmail', email);
      // Google and Apple both hand back the account holder's own name; it is
      // what Film Club should call them (bug report, 2026-08-20). Apple only
      // returns it on the very FIRST authorization, so a null here must not
      // wipe a name captured earlier — hence the guard.
      if (user?.displayName) context.commit('setUserRealName', user.displayName);
      // NOTE: this used to `dispatch` — but setDatabaseTopKey is a MUTATION,
      // never an action, so that dispatch was a silent no-op and state.databaseTopKey
      // stayed null through login. It only worked because the router guard
      // re-read the key from localStorage on the very next navigation and
      // committed it there. Committing directly makes the flow correct rather
      // than accidentally correct.
      context.commit('setDatabaseTopKey', email);
      window.localStorage.setItem('databaseTopKey', emailToDatabaseKey(email));
      // Kept alongside the key so the "Signed in as ..." line survives a
      // reload. state.databaseTopKey is sanitized and can't be turned back
      // into an address, so the raw email has to be stored separately.
      window.localStorage.setItem('userEmail', email);
      context.dispatch('initializeDB');
      pushRoute('/');
    },
    async login (context) {
      // Kept under its original name so existing callers keep working.
      return context.dispatch('loginWithGoogle');
    },
    async loginWithGoogle (context) {
      const result = await signInWithPopup(getAuth(), new GoogleAuthProvider());
      context.dispatch('completeLogin', result.user);
    },
    async loginWithApple (context) {
      // Apple is an OAuth provider rather than a first-class one in the
      // Firebase SDK. The email scope is required — without it Apple returns
      // no address at all and completeLogin would (correctly) refuse.
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');

      const result = await signInWithPopup(getAuth(), provider);
      context.dispatch('completeLogin', result.user);
    },
    // Automated-testing sign-in: scripts/mint-test-token.mjs mints a
    // short-lived Admin-SDK custom token for the dedicated tester account,
    // and /#/login?testToken=... hands it here. Safe by construction: a
    // custom token only signs in the account it was minted for, minting
    // requires the admin key, and the database rules scope any account to
    // its own email-derived branch — so this can never reach real data.
    async loginWithTestToken (context, token) {
      const result = await signInWithCustomToken(getAuth(), token);
      context.dispatch('completeLogin', result.user);
    },
    async loginWithEmail (context, { email, password }) {
      const result = await signInWithEmailAndPassword(getAuth(), email, password);
      context.dispatch('completeLogin', result.user);
    },
    async signUpWithEmail (context, { email, password }) {
      const result = await createUserWithEmailAndPassword(getAuth(), email, password);

      // Fire and forget, and deliberately not enforced anywhere yet — a library
      // is keyed by email address, so being able to prove you own that address
      // matters. Firebase's "one account per email" setting is what actually
      // stops someone registering an address that's already in use; this is the
      // groundwork for tightening that later without blocking sign-ups now.
      sendEmailVerification(result.user).catch((error) => {
        ErrorLogService.error('Could not send verification email', error);
      });

      context.dispatch('completeLogin', result.user);
    },
    async sendPasswordReset (context, email) {
      await sendPasswordResetEmail(getAuth(), email);
    },
    async logout (context) {
      await signOut(getAuth());
      // Otherwise signing back in to the same account in this tab finds the
      // marker still set and Film Club never listens again until a reload.
      context.dispatch('detachSocialListeners');
      window.localStorage.removeItem('databaseTopKey');
      window.localStorage.removeItem('userEmail');
      context.commit('setUserEmail', null);
      // The mutation clears the stored copy too — signing out must not leave
      // the previous account's name behind for whoever signs in next.
      context.commit('setUserRealName', null);
      context.commit('setDatabaseTopKey', null);
      await context.dispatch('resetLocalDB');
      pushRoute('/login');
    },
    // The router decides you're signed in synchronously from localStorage,
    // which is the only thing that ever gated access while the database rules
    // were open. Once they aren't, a stale key (session expired, password
    // changed, signed out on another device) would show an empty library with
    // no explanation. This reconciles the two once Firebase has had its say.
    async verifyRestoredSession (context) {
      const storedKey = window.localStorage.getItem('databaseTopKey');
      if (!storedKey) {
        return;
      }

      const user = await authReady;
      if (user) {
        // Keep the in-memory identity honest even if localStorage is stale —
        // the restored session is the authority on who you actually are.
        if (user.email && user.email !== context.state.userEmail) {
          context.commit('setUserEmail', user.email);
          window.localStorage.setItem('userEmail', user.email);
        }
        // Anyone already signed in when this shipped never went through
        // completeLogin again, so this is where their real name is picked up.
        if (user.displayName && user.displayName !== context.state.userRealName) {
          context.commit('setUserRealName', user.displayName);
        }
        return;
      }

      // Only act when we can actually tell a revoked session apart from a
      // flaky one. Firebase restores sessions from local persistence without
      // a network, so this is belt-and-braces rather than strictly required.
      if (!context.state.isOnline) {
        return;
      }

      // A warning: this is the routine, handled case (Sentry, 2026-10-11).
      // No context — the stored key is the person's email address.
      ErrorLogService.warn('Stored login found but no Firebase session — signing out');
      markSessionExpired();
      await context.dispatch('logout');
    },
    async resetLocalDB (context) {
      context.commit('setMovieLog', {});
      context.commit('setSettings', {});
      context.commit('setAcademyAwardWinners', {});
      // allAcademyAwards deliberately NOT reset here — unlike movieLog/
      // settings/academyAwardWinners (all scoped to whichever account this
      // is switching to/from), the full awards dataset isn't user-specific
      // at all, so there's no reason to discard and re-fetch ~11k records
      // just because the active account changed.

      await context.dispatch('initializeDB');
    },
    async initializeDB (context) {
      if (!context.getters.databaseTopKey) {
        return;
      }

      // Wait for Firebase to restore (or rule out) a persisted session before
      // attaching any listener, so reads always carry an auth token. Harmless
      // while the database rules are open; load-bearing once they aren't.
      //
      // Deliberately NOT gated on the result: the offline path below has to
      // keep working from the IndexedDB snapshot even when there's no live
      // session to restore, and the router owns the "you're signed out, go to
      // /login" decision (see verifyRestoredSession).
      // Start the local snapshot read NOW, in parallel with the auth restore,
      // instead of after it. Bug report (Matt, 2026-09-05): tapping a push
      // notification "shows me Cinema Roll and then immediately flashes to
      // the loading screen and then shows it again." iOS reloads a Home
      // Screen web app when a notification navigates it (the declarative
      // payload's `navigate` URL is mandatory), so the reload itself is the
      // OS's; what's ours is how long the reloaded page sits on the spinner.
      // It used to be auth restore + IndexedDB read back to back, for a
      // read that never needed a token — it's this device's own copy.
      // Now they overlap, and the spinner is delayed on Home so a warm
      // launch never shows it at all.
      const earlyKey = context.getters.databaseTopKey;
      let priorSnapshotPromise = listenersLiveFor(context, earlyKey) ? null : startSnapshotRead(context, earlyKey);

      await authReady;

      const topKey = context.getters.databaseTopKey;

      // Attach the live listeners exactly once per account key. The old
      // guard asked "does state already have data?" — which broke the day
      // game screens started committing a play-counter into settings at
      // mount: that scrap of local data read as "already loaded," the
      // settings listener never attached, and the whole session ran
      // without personalAwards (the vanished Insights pane). An explicit
      // flag can't be fooled by local writes. dbReadDenied still forces a
      // re-attach after sign-in, since a cancelled listener never refires.
      const listenersLive = listenersLiveFor(context, topKey);
      if (!listenersLive) {
        context.commit('setDbListenersAttachedFor', topKey);
        // The snapshot read started above, before auth settled. Only if the
        // account changed while auth was restoring (a dev-mode flip mid-boot)
        // does it need starting over for the right key.
        if (!priorSnapshotPromise || earlyKey !== topKey) {
          priorSnapshotPromise = startSnapshotRead(context, topKey);
        }
        let shadowCheckStarted = false;

        // Shared by both load modes: a cancelled listener means permission
        // denied under the locked-down rules — a dead/wrong session, or dev
        // mode pointing at a database this account can't read. The library
        // isn't empty, this device just can't see it — surface that
        // (LibraryAccessBanner) instead of leaving a silent empty grid.
        const onMovieLogCancelled = (error) => {
          reportRefusedListener('movieLog', error);
          context.commit('setDbReadDenied', true);
          // Whatever the snapshot fallback managed to show is all there is;
          // stop any loading state so the banner + cached view take over.
          context.commit('setDbLoaded', true);
        };

        // Delta sync phase 2/3 (2026-08-17): which listener attaches decides
        // what this launch downloads. launchPlan picks delta only when the
        // lastSync baseline AND a non-empty snapshot both exist and a full
        // resync isn't due; anything else — first run, lost snapshot, stale
        // baseline, any error below — lands on the full listener, which is
        // also where the shadow comparison keeps running (and recording
        // readings) at resync cadence.
        const attachFullListener = () => onValue(ref(db, `${topKey}/movieLog`), (snapshot) => {
          const data = snapshot.val();

          // Same library the launch snapshot already painted (same entry
          // count, same newest updatedAt, nothing of ours in flight): keep
          // that object rather than handing Home a new identity to rebuild
          // every derived table from - an 0.8s stall at phone speed ~2s
          // into every launch (2026-09-23 speed sweep, relaunch phases).
          // Every write stamps updatedAt, so an edit moves the max and a
          // deletion moves the count. The shadow check still runs.
          const current = context.state.movieLog;
          const unchanged = data && current && context.state.dbLoaded && !context.state.dbReadDenied &&
            !Object.keys(context.state.inFlightWrites || {}).length &&
            Object.keys(data).length === Object.keys(current).length &&
            maxUpdatedAt(data) === maxUpdatedAt(current);

          if (data && unchanged) {
            if (!shadowCheckStarted) {
              shadowCheckStarted = true;
              context.dispatch('runDeltaShadowCheck', { fresh: data, priorSnapshotPromise, topKey });
            }
            context.dispatch('replayPendingWrites', 'movieLog');
            context.commit('setDbReadDenied', false);
            return;
          }

          if (data) {
            // Delta sync SHADOW check (phase 1): reconstruct the library
            // from prior-snapshot + delta query and diff it against this
            // full download, acting on neither. Once per session, and
            // dispatched BEFORE saveSnapshot below re-persists the fresh
            // data (the prior-snapshot read transaction is already open, so
            // ordering here is belt-and-braces, not load-bearing).
            if (!shadowCheckStarted) {
              shadowCheckStarted = true;
              context.dispatch('runDeltaShadowCheck', { fresh: data, priorSnapshotPromise, topKey });
            }
            // Same in-flight re-top as the settings listener below — a
            // tiebreak's score adjustments are movieLog writes, and a
            // snapshot predating them would otherwise briefly restore the
            // pre-tiebreak scores (re-showing the tie).
            context.commit('setMovieLog', reapplyInFlightWrites(context.state, 'movieLog', data));
            saveSnapshot(topKey, 'movieLog', data);
            // And the durable-queue re-top: on a reconnecting launch this
            // first server snapshot predates the background flush, so
            // without this the offline-made rating blinked out of the
            // library until the flush's own refire brought it back.
            context.dispatch('replayPendingWrites', 'movieLog');
          }
          context.commit('setDbReadDenied', false);
          context.commit('setDbLoaded', true);
        }, onMovieLogCancelled);

        // The delta path: a startAt(lastSync) query listener. Every write
        // stamps updatedAt with serverTimestamp(), which is always >= the
        // baseline, so the one query delivers BOTH the catch-up set at
        // attach AND every live change for the rest of the session — the
        // full listener's job at a fraction of the download. Deletions
        // can't appear in a "what changed" query, so the (tiny) tombstone
        // node is listened to alongside and applied by
        // reconstructFromDelta's newer-stamp-wins rule.
        const attachDeltaListener = (meta, priorSnapshot) => {
          let latestDelta = null; // null = query hasn't fired; {} = fired, nothing changed
          let latestTombstones = {};

          const applyReconstruction = () => {
            if (latestDelta === null) return;
            // Nothing changed since the snapshot this launch painted from:
            // keep that very object. Committing an identical reconstruction
            // gave the library a new identity, and everything derived from
            // it (scores, search fields, counts, the grid) was rebuilt a
            // second time ~2s into every launch - an 0.8s stall at phone
            // speed (2026-09-23 speed sweep, relaunch phases).
            // startAt(lastSync) is inclusive, so the newest entry of the
            // snapshot always comes back in the delta: "nothing new" means
            // every delta entry is one the snapshot already holds at the
            // same updatedAt (every write stamps it).
            const deltaAlreadyHeld = Object.entries(latestDelta).every(([key, entry]) =>
              priorSnapshot?.[key] && priorSnapshot[key].updatedAt === entry?.updatedAt);
            const nothingNew = deltaAlreadyHeld &&
              Object.keys(latestTombstones).length === 0 &&
              !Object.keys(context.state.inFlightWrites || {}).length &&
              context.state.movieLog === priorSnapshot;
            if (nothingNew) {
              context.dispatch('replayPendingWrites', 'movieLog');
              context.commit('setDbReadDenied', false);
              context.commit('setDbLoaded', true);
              return;
            }
            const reconstructed = reconstructFromDelta(priorSnapshot, latestDelta, latestTombstones);
            // Same in-flight + durable-queue re-tops as the full listener:
            // the snapshot half of the reconstruction predates this
            // session's writes by definition.
            context.commit('setMovieLog', reapplyInFlightWrites(context.state, 'movieLog', reconstructed));
            saveSnapshot(topKey, 'movieLog', reconstructed);
            // Snapshot and lastSync advance TOGETHER in this same pass —
            // the invariant that makes next launch's startAt(lastSync)
            // sound. (A local serverTimestamp echo can briefly overstate
            // lastSync by clock skew; the periodic full resync bounds it.)
            const newLastSync = maxUpdatedAt(reconstructed);
            if (newLastSync != null && newLastSync !== meta.lastSync) {
              saveSnapshot(topKey, 'deltaSyncMeta', { ...meta, lastSync: newLastSync, savedAt: Date.now() });
            }
            context.dispatch('replayPendingWrites', 'movieLog');
            context.commit('setDbReadDenied', false);
            context.commit('setDbLoaded', true);
          };

          onValue(ref(db, `${topKey}/movieLogDeletions`), (snap) => {
            latestTombstones = snap.val() || {};
            applyReconstruction();
          }, (error) => {
            // Tombstones are advisory; a denied read here (same rules gate
            // as movieLog, so this shouldn't happen alone) costs deletions
            // propagating until the next full resync, not the library.
            console.warn('movieLogDeletions listener cancelled:', error);
          });

          onValue(query(ref(db, `${topKey}/movieLog`), orderByChild('updatedAt'), startAt(meta.lastSync)), (snap) => {
            latestDelta = snap.val() || {};
            applyReconstruction();
          }, onMovieLogCancelled);
        };

        (async () => {
          try {
            const meta = await loadSnapshot(topKey, 'deltaSyncMeta').catch(() => null);
            // Only a launch that MIGHT go delta waits on the (big) snapshot
            // read; with no baseline the full listener attaches without
            // gating network on IndexedDB.
            if (meta?.lastSync != null) {
              const priorSnapshot = await priorSnapshotPromise;
              const plan = launchPlan(meta, priorSnapshot);
              if (plan.mode === 'delta') {
                attachDeltaListener(meta, priorSnapshot);
                return;
              }
              console.info(`[delta-sync] full download this launch: ${plan.reason}`);
            }
          } catch (error) {
            console.warn('[delta-sync] falling back to full download:', error);
          }
          attachFullListener();
        })();

        loadSnapshot(topKey, 'externalFriends').then((cached) => {
          if (cached?.profiles) context.commit('restoreExternalFriends', cached);
          return null;
        }).catch(() => {});
        context.dispatch('ensurePersonalAwards');
        // Movie Hat reads resume conditionally from the last launch's ETags.
        loadSnapshot(topKey, 'movieHatContents').then((cached) => {
          if (cached?.cache && !context.state.movieHatContentsAt) context.commit('setMovieHatContents', cached);
          return null;
        }).catch(() => {});
        loadSnapshot(topKey, 'movieHatSummaries').then((cached) => {
          if (cached && !Object.keys(context.state.movieHatSummaryCache || {}).length) context.commit('setMovieHatSummaryCache', cached);
          return null;
        }).catch(() => {});
        loadSnapshot(topKey, 'settings').then(async (cached) => {
          // settingsLoaded (not key-counting) decides whether the snapshot
          // may apply: local write scraps can predate it. The snapshot
          // replaces them wholesale, and the queue replay below restores
          // them on top — they were durably enqueued by the same writes
          // that created them.
          if (cached && !context.state.settingsLoaded) {
            context.commit('setSettings', cached);
            context.commit('setSettingsLoaded', true);
            await context.dispatch('replayPendingWrites', 'settings');
          }
          return null;
        }).catch(() => {});

        onValue(ref(db, `${topKey}/settings`), (snapshot) => {
          const data = snapshot.val();

          if (data) {
            // Snapshot the raw server data offline, but show the version
            // re-topped with still-unconfirmed local writes (see
            // reapplyInFlightWrites) so a just-made change can't flicker back.
            context.commit('setSettings', reapplyInFlightWrites(context.state, 'settings', data));
            context.commit('setSettingsLoaded', true);
            saveSnapshot(topKey, 'settings', data);
            context.dispatch('replayPendingWrites', 'settings');
            if (data.personalAwards) context.dispatch('migratePersonalAwards', data.personalAwards);
          }
        }, (error) => {
          // Same denial signal as the movieLog listener above (the rules
          // gate both identically); committing the flag twice is harmless.
          reportRefusedListener('settings', error);
          context.commit('setDbReadDenied', true);
        });
      }
      const academyAwardWinnersHasData = Boolean(Object.keys(context.state.academyAwardWinners).length);
      if (!academyAwardWinnersHasData) {
        // This block enriches ~98 Best Picture winners with a TMDB call EACH,
        // sequentially. It used to be guarded only on in-memory state, so every
        // cold launch re-ran all 98 requests — the single largest source of API
        // traffic in the app. The list changes once a year, so it caches like
        // movieLog/settings do, keyed 'global' because it isn't user-specific.
        const cached = await loadSnapshot('global', BEST_PICTURE_SNAPSHOT);
        if (cached && Object.keys(cached).length) {
          context.commit('setAcademyAwardWinners', cached);
        }
      }

      // One download serves both the Best Picture list and the full list.
      let awardsDataset = null;
      if (!Object.keys(context.state.academyAwardWinners).length) {
        try {
          awardsDataset = awardsDataset || await fetchAcademyAwardsDataset();
          const data = awardsDataset.filter((item) => String(item.category).toLowerCase() === 'best picture').map((item) => {
            return {
              ...item,
              isWinner: ['TRUE', '1', true].includes(item.isWinner)
            }
          }).filter((item) => item.isWinner);

          const bestPictureWinners = [];

          for (const movieEntry of data) {
            try {
              const movieResponse = await axios.get(`https://api.themoviedb.org/3/movie/${movieEntry.tmdb}?api_key=${process.env.VUE_APP_TMDB_API_KEY}`);
              bestPictureWinners.push({
                ...movieResponse.data,
                academyAwardsYear: movieEntry.year
              });
            } catch (error) {
              console.error(error);
              ErrorLogService.error('Error fetching movie data for Academy Award winner:', movieEntry, error);
            }
          }

          const winners = { bestPicture: bestPictureWinners };
          context.commit('setAcademyAwardWinners', winners);
          // Only cache a complete-looking result; a partial fetch shouldn't be
          // baked in as the answer for the next month.
          if (bestPictureWinners.length) {
            saveSnapshot('global', BEST_PICTURE_SNAPSHOT, winners);
          }
        } catch (error) {
          console.error('Failed to get awards data:', error);
          ErrorLogService.error('Failed to get awards data:', error);
        }
      }
      // Full, all-category, all-year Academy Awards dataset — "there aren't
      // that many Academy Awards, it'd be like one JSON thing we could pull
      // down... store it locally so we can use it wherever we want to."
      // ~11k raw records (wins AND nominations, every category since the
      // 1st ceremony), now the static /data/academy-awards.json (see ACADEMY_AWARDS_URL),
      // ~5.5MB. NOT TMDB-enriched
      // per record (unlike academyAwardWinners' Best Picture list) — that
      // would mean a per-record TMDB fetch across ~11k rows, defeating the
      // "not that big" simplicity this is explicitly meant to have.
      // Consumers needing more than the raw tmdb id (e.g. a poster) already
      // have the movie's own local entry to fall back to when it's in the
      // library, or can look it up live for the rare case it isn't.
      const allAcademyAwardsHasData = context.state.allAcademyAwards.length > 0;
      if (!allAcademyAwardsHasData) {
        // ~5.25MB. This previously kicked off the cache read and the network
        // fetch TOGETHER, so the snapshot only ever won a race to first paint
        // — the download happened on every launch anyway. Awaiting the cache
        // first means a device that already has it downloads nothing.
        const cached = await loadSnapshot('global', ACADEMY_SNAPSHOT);
        if (cached && cached.length) {
          context.commit('setAllAcademyAwards', cached);
        }
      }

      if (context.state.allAcademyAwards.length === 0) {
        try {
          awardsDataset = awardsDataset || await fetchAcademyAwardsDataset();
          const data = awardsDataset.map((record) => ({
            ...record,
            isWinner: ['TRUE', '1', true].includes(record.isWinner),
            isActing: ['TRUE', '1', true].includes(record.isActing)
          }));
          context.commit('setAllAcademyAwards', data);
          if (data.length) {
            saveSnapshot('global', ACADEMY_SNAPSHOT, data);
          }
        } catch (error) {
          console.error('Failed to get the full Academy Awards dataset:', error);
          ErrorLogService.error('Failed to get the full Academy Awards dataset:', error);
        }
      }

      // offline-killed session: flush if already online (no-ops otherwise),
      // and always refresh the reconciliation banner state regardless of
      // connectivity.
      context.dispatch('flushPendingWrites');
      context.dispatch('refreshPendingReconciliations');
    },
    // todo: should I delete this? Nothing is calling it but it seems like something I kind of need...
    async initiateNewDatabase (context) {
      if (!context.getters.databaseTopKey) {
        return;
      }

      const newDB = {
        movieLog: {},
        settings: {
          tags: {
            "viewing-tags": { title: "default viewing tag" },
          }
        }
      }

      set(ref(db, `${context.getters.databaseTopKey}`), newDB);

      context.dispatch('initializeDB');
    },
    async setDBValue (context, dbEntry) {
      const now = Date.now();
      const timeSinceLastSave = now - context.state.lastSaveTime;
      const isSamePath = context.state.lastSavePath === dbEntry.path;

      // Simple debounce: skip if saving same path within 1 second
      if (isSamePath && timeSinceLastSave < 1000) {
        return;
      }

      await performDatabaseWrite(context, dbEntry);
    },
    // The phase-2 escape hatch ("Refresh library from server", Settings →
    // Maintenance): wipe the delta baseline and reload, so the next launch
    // is a guaranteed full download (launchPlan: 'no-meta') that rebuilds
    // snapshot + lastSync from scratch. For the invisible failure mode —
    // "this movie looks stale/missing and I don't trust it".
    async forceFullLibraryRefresh (context) {
      const topKey = context.getters.databaseTopKey;
      try {
        if (topKey) await saveSnapshot(topKey, 'deltaSyncMeta', null);
      } catch (error) {
        console.warn('forceFullLibraryRefresh: could not clear baseline:', error);
      }
      window.location.reload();
    },
    // Delta sync shadow comparison. In phase 1 it ran on every launch; since
    // phase 2/3 (2026-08-17) it runs on every FULL launch (first run, lost
    // snapshot, periodic resync, forced refresh) — the launches that still
    // have both sides to compare. Divergence is the thing this exists to
    // catch — it logs loudly (console
    // + ErrorLogService, visible in the in-app error log) and stashes a
    // report in state; agreement logs quietly. Never throws: any failure
    // here (offline probe, missing meta, first run) just means "no
    // comparison this launch."
    async runDeltaShadowCheck (context, { fresh, priorSnapshotPromise, topKey }) {
      try {
        const meta = await loadSnapshot(topKey, 'deltaSyncMeta');
        const priorSnapshot = await priorSnapshotPromise;
        const newLastSync = maxUpdatedAt(fresh);

        if (meta?.lastSync != null && priorSnapshot) {
          // startAt is inclusive, so the boundary entry is re-fetched —
          // harmless (the merge replaces it with an identical copy) and
          // safer than +1 arithmetic against equal-stamp writes.
          const [deltaSnap, tombstoneSnap] = await Promise.all([
            get(query(ref(db, `${topKey}/movieLog`), orderByChild('updatedAt'), startAt(meta.lastSync))),
            get(ref(db, `${topKey}/movieLogDeletions`))
          ]);
          const deltaEntries = deltaSnap.val() || {};
          const reconstructed = reconstructFromDelta(priorSnapshot, deltaEntries, tombstoneSnap.val() || {});
          const report = {
            ...diffLibraries(fresh, reconstructed),
            deltaEntryCount: Object.keys(deltaEntries).length,
            lastSyncUsed: meta.lastSync,
            checkedAt: Date.now()
          };
          if (report.stale.length) {
            // Self-explaining divergences: stamps on both sides, whether the
            // delta query actually returned the key, and which fields differ.
            report.staleDetail = report.stale.map((dbKey) =>
              describeStaleEntry(fresh[dbKey], reconstructed[dbKey], deltaEntries, dbKey));
          }
          // A save that landed between the two reads is the delta being
          // ahead, not wrong (isWriteRaceOnly) — quiet, not a Sentry event.
          report.race = isWriteRaceOnly(report);
          report.summary = summarizeShadowReport(report);
          context.commit('setDeltaShadowReport', report);

          if (report.identical) {
            console.info(`[delta-shadow] identical: ${report.compared} entries reconstructed from snapshot + ${report.deltaEntryCount} delta entries`);
            ErrorLogService.info?.(`[delta-shadow] identical (${report.compared} entries, ${report.deltaEntryCount} via delta)`);
          } else if (report.race) {
            console.info(`[delta-shadow] a save landed between the two reads: ${report.summary}`);
            ErrorLogService.info?.(`[delta-shadow] write race, not a divergence: ${report.summary}`, report);
          } else {
            console.warn(`[delta-shadow] DIVERGENCE: ${report.summary}`, report);
            ErrorLogService.error(`[delta-shadow] delta reconstruction diverged from full download: ${report.summary}`, report);
          }

          // Shadow mode existed to answer "is delta reconstruction safe to
          // switch on", and the answer was only ever visible in localStorage
          // error logs on the device that ran the check — so the decision
          // depended on somebody reading their own phone. Each reading is
          // recorded in the account now, so the question can be settled from
          // the data (scripts/delta-shadow-report.mjs).
          context.dispatch('recordDeltaShadowReading', report);
        }

        // Either way, this launch's full download is the new baseline, and
        // counts as the periodic full resync (launchPlan's lastFullSyncAt).
        if (newLastSync != null) {
          await saveSnapshot(topKey, 'deltaSyncMeta', { lastSync: newLastSync, savedAt: Date.now(), lastFullSyncAt: Date.now() });
        }
      } catch (error) {
        // Shadow mode must never affect the real load path.
        console.warn('[delta-shadow] check skipped:', error);
      }
    },
    /**
     * A compact record of one shadow check, kept in the account so the
     * phase-2 decision can be made from readings rather than recollection.
     *
     * Diagnostic and re-derivable, so it uses the plain write path rather
     * than the durable queue — losing one costs nothing.
     */
    async recordDeltaShadowReading (context, report) {
      const topKey = context.getters.databaseTopKey;
      if (!topKey || !report) return;

      try {
        const reading = {
          at: report.checkedAt || Date.now(),
          identical: Boolean(report.identical),
          compared: report.compared ?? null,
          deltaEntries: report.deltaEntryCount ?? null,
          // Counts only: the keys themselves say nothing useful later, and
          // this should stay small.
          missing: report.missing?.length || 0,
          stale: report.stale?.length || 0,
          extra: report.extra?.length || 0,
          race: Boolean(report.race),
          summary: report.summary || null
        };

        const readings = context.state.settings?.deltaShadowReadings || {};
        const keys = Object.keys(readings).sort();
        const updates = { [`settings/deltaShadowReadings/${reading.at}`]: reading };

        // A rolling window: enough to see a trend, not enough to grow.
        keys.slice(0, Math.max(0, keys.length - 29)).forEach((old) => {
          updates[`settings/deltaShadowReadings/${old}`] = null;
        });

        await context.dispatch('updateDatabaseEntriesNow', updates);
      } catch (error) {
        // Diagnostics must never affect the load path.
        console.warn('[delta-shadow] could not record the reading:', error);
      }
    },
    // Re-applies every still-queued durable write for `root` ('movieLog' or
    // 'settings') to LOCAL state, oldest first. Bug report: a movie rated
    // offline "seemed to appear... but when I closed the app and reopened
    // it, the rating was gone." The IndexedDB snapshot only ever mirrors the
    // last SERVER state (saveSnapshot fires from the live listener alone),
    // so an offline relaunch — or the first server snapshot after
    // reconnecting, arriving before the flush completes — showed the library
    // WITHOUT anything rated since the last sync, even though its durable
    // queue entry was sitting right there. Replaying the queue on top closes
    // that: local state = last known server state + everything still
    // pending. Safe to re-run any time: a queue entry is removed once the
    // server confirms it, so replaying can only re-assert values the server
    // hasn't caught up to yet (a written-but-unreconciled placeholder stays
    // queued, but its queued value matches what was written — idempotent).
    async replayPendingWrites (context, root) {
      const pending = await listPendingWrites();
      pending
        .filter((entry) => entry?.dbEntry?.path && entry.dbEntry.path.split('/')[0] === root)
        .forEach((entry) => context.commit('applyDbPathLocally', entry.dbEntry));
    },
    // Recomputes state.pendingReconciliations from the pendingWriteQueue -
    // cheap, purely local (IndexedDB) read, safe to call regardless of
    // connectivity so Home.vue's "needs a match" banner reflects reality
    // even before the next successful flush.
    async refreshPendingReconciliations (context) {
      const pending = await listPendingWrites();
      context.commit('setPendingReconciliations', pending.filter((entry) => entry.type === 'placeholder' && entry.status !== 'reconciled'));
      if (context.state.isOnline) {
        context.dispatch('autoReconcilePlaceholders', pending);
      }
    },
    // Placeholders whose movie the user already picked (pendingTmdbId, set by
    // AddRating's lie-fi fallback) finish themselves as soon as TMDB answers:
    // the details are fetched, the entry replaced in place, the badge never
    // shown. One pass at a time; a failure leaves that entry for next time.
    async autoReconcilePlaceholders (context, pending = null) {
      if (autoReconcileRunning || !context.state.isOnline) return;
      const candidates = placeholdersReadyToFinish(pending || await listPendingWrites());
      if (!candidates.length) return;
      autoReconcileRunning = true;
      try {
        for (const entry of candidates) {
          if (!context.state.isOnline) break;
          try {
            await finalizePlaceholder(context, entry, entry.dbEntry.value.movie.pendingTmdbId);
            context.commit('flashSaved', `${entry.title || 'A rating'} matched`);
          } catch (error) {
            console.warn('Could not finish a placeholder rating automatically; it stays on the review list:', error?.message);
          }
        }
      } finally {
        autoReconcileRunning = false;
      }
    },
    // Processes the durable offline-write queue (see pendingWriteQueue.js) in
    // order once online: attempts each entry's write, removes 'write' entries
    // on success, marks 'placeholder' entries written (kept for
    // reconciliation), and records attempts/lastError on failure without
    // aborting the rest of the pass. Triggered from App.vue's 'online' event
    // + its other iOS-reliability triggers/interval, and once from
    // initializeDB on a cold start that's already online.
    async flushPendingWrites (context) {
      // state.isOnline, not navigator.onLine: while the connection is
      // stalled (lie-fi) every attempt would just burn the write timeout.
      // networkHealth's probe dispatches this again the moment it clears.
      if (!context.state.isOnline) return;
      if (context.state.isFlushingPendingWrites) return;
      if (!context.getters.databaseTopKey) return;

      context.commit('setIsFlushingPendingWrites', true);
      try {
        const pending = await listPendingWrites();

        for (const entry of pending) {
          if (!entry.dbEntry) continue;
          // A 'placeholder' entry stays queued (for reconciliation tracking)
          // even after its write already succeeded once - written:true means
          // there's nothing left to (re-)attempt here, only actual
          // reconciliation (ReconcilePlaceholder.vue) removes it. Without
          // this, an unreconciled placeholder would get redundantly
          // re-written to Firebase on every single background sweep for as
          // long as it sits unreconciled.
          if (entry.type === 'placeholder' && entry.written) continue;

          try {
            await performDatabaseWrite(context, entry.dbEntry);
            if (entry.type === 'write') {
              await removePendingWrite(entry.id);
            } else {
              await updatePendingWrite(entry.id, { written: true });
            }
          } catch (error) {
            const attempts = (entry.attempts || 0) + 1;
            await updatePendingWrite(entry.id, { attempts, lastError: String(error) });
            if (attempts === QUEUED_WRITE_REPORT_AFTER) {
              console.error(`Queued write still failing after ${attempts} attempts:`, entry.dbEntry.path, error);
            }
          }
        }

        await context.dispatch('refreshPendingReconciliations');
      } finally {
        context.commit('setIsFlushingPendingWrites', false);
      }
    },
    // Bug fix (Jul 2026), superseding an earlier fix that turned out to be
    // insufficient: AddRating.js originally routed EVERY save (online or
    // not) through the offline queue + a shared, guarded flush action. A
    // live incident showed the actual bug this created - a movie rated
    // normally, while online, vanished, because navigating home right after
    // triggers another background flush pass (via initializeDB), and
    // whichever pass's isFlushingPendingWrites guard "won" the race could
    // silently skip the brand-new entry with nothing left to retry it
    // before the app was closed. An interim fix (a dedicated, guard-free
    // flushSingleEntry action) closed that specific race but kept the same
    // fundamental shape: every online save STILL depended on a queue write
    // succeeding and a follow-up action running correctly.
    //
    // This action is the actual fix: a raw, direct write with NO queue
    // involvement at all - no dependency on IndexedDB succeeding, no shared
    // state, nothing another concurrent call could interfere with. When
    // online (the common case), AddRating.js/ReconcilePlaceholder.vue now
    // await THIS directly and only fall back to the durable queue if it
    // throws (a genuine failure/timeout) or the device is offline to begin
    // with - restoring the same "the write is confirmed before we tell the
    // user it succeeded" guarantee the app had before the offline-rating
    // feature existed, for the case that actually broke.
    async writeDatabaseEntryNow (context, dbEntry) {
      await performDatabaseWrite(context, dbEntry);
      // A brand-new rating comes through HERE, not writeDurably — AddRating
      // owns its own durability machinery — so the staleness fix has to hook
      // both. Double-firing is fine: both publishes are debounced.
      if (typeof dbEntry?.path === 'string' && dbEntry.path.startsWith('movieLog')) {
        context.dispatch('scheduleSocialPublish');
        context.dispatch('scheduleMirrorPublish');
      }
    },
    // General-purpose "offline-safe write" for the features that don't need
    // AddRating.js's placeholder/reconciliation machinery, just its core
    // durability guarantee (bug report, Jul 2026: "updates to the stickiness
    // rating and the tiebreaker values and any [Personal Awards] changes...
    // should be able to be done offline and then push to the database when
    // I'm back online"). Same overall shape as AddRating.js's write path -
    // optimistic local commit, THEN durably enqueue BEFORE attempting -
    // generalized via applyDbPathLocally so it isn't limited to movieLog/*
    // paths the way setMovieLogEntry is.
    //
    // Deliberately does NOT re-throw on a failed online attempt (unlike
    // AddRating.js's non-placeholder path) - none of these three callers had
    // dedicated per-write error UI before this, and their existing
    // try/catch+console.error/ErrorLogService handling is the right level of
    // visibility to preserve. The durable queue entry is the real safety net
    // either way; a failed attempt just means the background flush
    // (App.vue's triggers / initializeDB) retries it, same as offline.
    async writeDurably (context, dbEntry) {
      context.commit('applyDbPathLocally', dbEntry);
      context.commit('trackInFlightWrite', dbEntry);
      // Any change to the library makes the published profile stale. Hooked
      // here rather than in a screen because a rating can be saved from
      // several places, and Home may not even be mounted.
      if (typeof dbEntry?.path === 'string' && dbEntry.path.startsWith('movieLog')) {
        context.dispatch('scheduleSocialPublish');
        context.dispatch('scheduleMirrorPublish');
      }

      // Bug report ("I get the tie break message again just for a second or
      // two"): this used to `await enqueueWrite(...)` BEFORE issuing the
      // network write, which meant every Firebase set() sat behind an
      // IndexedDB open + full-queue scan + put. With several writeDurably
      // calls back to back (a tiebreak fires 3+), an early one could reach
      // the server and bounce back an onValue snapshot while a later one
      // hadn't even been sent yet. Kicking both off together keeps the same
      // durability (the IndexedDB record still lands regardless of what
      // happens to the network write) without the network write inheriting
      // IndexedDB's latency. trackInFlightWrite above covers the remaining
      // in-flight window.
      const queuedPromise = enqueueWrite({ type: 'write', dbEntry }, { online: context.state.isOnline });

      try {
        // Offline: nothing to attempt, just make sure it's durably queued.
        if (!context.state.isOnline) {
          await queuedPromise;
          return;
        }

        await context.dispatch('writeDatabaseEntryNow', dbEntry);
        const queuedRecord = await queuedPromise;
        if (queuedRecord) await removePendingWrite(queuedRecord.id);
      } catch (error) {
        // Normally already durably queued regardless of this failure. If the
        // queue couldn't take it either (enqueueWrite only warned, expecting
        // the server copy), the change now exists only on this screen.
        const queuedRecord = await queuedPromise.catch(() => null);
        if (!queuedRecord) {
          console.error('writeDurably: direct write failed and the device queue is unavailable, change is not saved:', dbEntry.path, error);
          return;
        }
        const log = isWriteTimeout(error) ? console.warn : console.error;
        log('writeDurably: direct write failed, will retry via the background queue:', dbEntry.path, error);
      } finally {
        // Always untrack, including the offline path — otherwise
        // inFlightWrites would grow unboundedly across an offline session.
        // The local value still stands on its own (applyDbPathLocally
        // committed it) and the durable queue owns getting it to the server.
        context.commit('untrackInFlightWrite', dbEntry.path);
      }
    },
    // Atomic multi-path write via Firebase's update() (not set()) - `updates`
    // is a flat object whose keys are full paths relative to the account
    // root, e.g. { 'movieLog/key1/movie/budget': 100, 'movieLog/key2/movie/revenue': 200 }.
    // Lets a caller patch many different movies' individual fields in ONE
    // network round trip / ONE onValue listener refire, instead of one write
    // per field per movie - the remote-write half of the same batching fix
    // setMovieLogEntries is the local half of (see its own comment). Used
    // for bulk background patches (e.g. backfillBoxOffice.js) where
    // minimizing the NUMBER of separate writes matters far more than the
    // single-entry durability guarantees a user-authored rating needs.
    async updateDatabaseEntriesNow (context, updates) {
      // Same change tracking as performDatabaseWrite, but a batch can touch
      // many movies at once so each one gets its own updatedAt. See
      // stampUpdatesForBatch for the overlapping-path trap it avoids.
      const cleanedUpdates = removeNaNAndUndefined(stampUpdatesForBatch(updates, serverTimestamp()));
      await withTimeout(
        update(ref(db, context.getters.databaseTopKey), cleanedUpdates),
        DATABASE_WRITE_TIMEOUT_MS,
        `Database batch update timed out after ${DATABASE_WRITE_TIMEOUT_MS}ms`
      );
    },
    // ------------------------------------------------------------------
    // Film Club Interchange. Publishing lets a friend on another app
    // subscribe to this library; subscribing pulls THEIR feed and turns it
    // into the same profile shape Film Club renders, so a Movie Log friend
    // is indistinguishable from a Cinema Roll one. See interchange.js.
    async ensureClubFeedKey (context) {
      const existing = context.state.settings?.clubFeedKey;
      if (typeof existing === 'string' && existing.length >= 16) return existing;
      const bytes = new Uint8Array(16);
      (window.crypto || window.msCrypto).getRandomValues(bytes);
      const key = Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
      await context.dispatch('writeDurably', { path: 'settings/clubFeedKey', value: key });
      return key;
    },
    async publishClubFeed (context) {
      const me = context.state.databaseTopKey;
      const secret = context.state.settings?.clubFeedKey;
      if (!me || !secret || !context.state.dbLoaded) return;
      const entries = context.getters.allMediaAsArray;
      if (!entries.length) return;
      if (!(await personalAwardsReadyToPublish(context))) return;
      const feed = toInterchange(entries, getRating, {
        name: context.getters.socialSettings.displayName || 'A Cinema Roll user',
        awards: awardsByMovie(context.state.settings?.personalAwards),
        awardsName: awardNameWithThe(context.state.settings?.personalAwardName)
      });
      // End of day for friends on other apps (2026-10-01): the live feed is
      // parked where only I can read it, and the push Lambda publishes the
      // end-of-day copy to the public path (dayFeedFrom in pushCadence.js).
      if (context.state.settings?.clubFeedTiming === 'day') {
        await update(ref(db, `social/clubFeedLive/${me}`), {
          feed,
          secret,
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'
        });
        return;
      }
      // Removed first, so a sweep already under way finds nothing to release.
      await set(ref(db, `social/clubFeedLive/${me}`), null);
      // v2 (Brian's sync guide, 2026-10-06): the legacy body, the snapshot's
      // changed records, one journal batch and the metadata in ONE update.
      // The head is read fresh and rebuilt on any mismatch — the end-of-day
      // Lambda is the other writer. Our own last publish is kept so the
      // snapshot usually need not be read back.
      const lastPublished = await loadSnapshot(me, 'clubFeedPublished').catch(() => null);
      const result = await publishFeedV2({
        owner: me,
        secret,
        feed,
        databaseUrl: DATABASE_URL,
        lastPublished: lastPublished?.secret === secret ? lastPublished : null,
        io: {
          get: async (path) => (await get(ref(db, path))).val(),
          firstIndexKeys: async (path) => Object.keys((await get(query(ref(db, path), orderByKey(), limitToFirst(2)))).val() || {}),
          update: (updates) => update(ref(db), updates),
          newKey: () => push(ref(db, `clubFeedSync/${me}/${secret}/changes`)).key,
          randomHex: () => hexToken(crypto.getRandomValues(new Uint8Array(16)))
        }
      });
      saveSnapshot(me, 'clubFeedPublished', { secret, meta: result.meta, movies: result.movies }).catch(() => {});
      // The feed moved: tell negotiated friends (§6.3), after the write landed.
      if (result.mode !== 'unchanged') context.dispatch('sendClubNotices', { revision: result.meta.revision });
    },
    // One switch for every friend on another app: they all read the one feed.
    async setClubFeedTiming (context, timing) {
      await context.dispatch('writeDurably', { path: 'settings/clubFeedTiming', value: timing === 'day' ? 'day' : null });
      await context.dispatch('publishClubFeed');
    },
    // Cross-app discovery is its own opt-in: this row is PUBLICLY readable,
    // which is more exposure than the in-app directory (auth required), so
    // it is never published without an explicit choice.
    async setCrossAppDiscovery (context, enabled) {
      await context.dispatch('writeDurably', { path: 'settings/crossAppDiscovery', value: Boolean(enabled) });
      if (enabled) {
        await context.dispatch('publishDirectoryEntry');
      } else {
        const me = context.state.databaseTopKey;
        if (me) await set(ref(db, `clubDirectory/${me}`), null);
      }
    },
    async publishDirectoryEntry (context) {
      const me = context.state.databaseTopKey;
      if (!me || isQaAccountKey(me) || !context.state.settings?.crossAppDiscovery) return;
      const invite = await context.dispatch('createClubInvite');
      if (!invite) return;
      const entry = buildDirectoryEntry({
        handle: context.state.settings?.clubHandle || me,
        name: context.getters.socialSettings.displayName,
        inboxUrl: invite.inboxUrl
      });
      if (entry) await set(ref(db, `clubDirectory/${me}`), entry);
    },
    // Everyone discoverable on the apps we federate with.
    async fetchFederatedDirectory (context) {
      context.commit('setFederatedDirectoryLoading', true);
      try {
        const lists = await Promise.all(FEDERATED_APPS.map(async (app) => {
          try {
            const response = await fetch(app.directoryUrl, { cache: 'no-store' });
            if (!response.ok) throw new Error(`directory responded ${response.status}`);
            return normalizeDirectory(await response.json(), { app: app.id });
          } catch (error) {
            console.warn(`[film-club] ${app.name} directory unavailable:`, error.message);
            return [];
          }
        }));
        context.commit('setFederatedDirectory', lists.flat());
      } finally {
        context.commit('setFederatedDirectoryLoading', false);
      }
    },
    // Add someone straight from the directory — no links, no pasting.
    async requestFriendFromDirectory (context, entry) {
      if (!entry?.inboxUrl) return { ok: false, error: 'That person has no inbox.' };
      try {
        const sent = await context.dispatch('sendOwnFeed', { to: entry.inboxUrl });
        if (!sent) return { ok: false, error: 'Could not create your own feed.' };
        // Remember we asked, so the directory can hide them.
        await context.dispatch('writeDurably', {
          path: `settings/clubRequestsSent/${entry.handle}`,
          value: { name: entry.name, app: entry.app, inboxUrl: entry.inboxUrl, at: Date.now() }
        });
        return { ok: true };
      } catch (error) {
        return { ok: false, error: `Could not reach them: ${error.message}` };
      }
    },
    async ensureClubInviteCode (context) {
      const existing = context.state.settings?.clubInviteCode;
      if (typeof existing === 'string' && existing.length >= 8) return existing;
      const bytes = new Uint8Array(8);
      (window.crypto || window.msCrypto).getRandomValues(bytes);
      const code = Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
      await context.dispatch('writeDurably', { path: 'settings/clubInviteCode', value: code });
      return code;
    },
    // Everything someone on another app needs: subscribe to me here, ask to
    // be added there. Creates the feed and invite code on first use.
    async createClubInvite (context) {
      const accountKey = context.state.databaseTopKey;
      if (!accountKey) return null;
      await context.dispatch('ensureClubFeedKey');
      await context.dispatch('publishClubFeed');
      const inviteCode = await context.dispatch('ensureClubInviteCode');
      return buildInvite({
        accountKey,
        inviteCode,
        feedUrl: `${DATABASE_URL}/clubFeed/${accountKey}/${context.state.settings?.clubFeedKey}.json`,
        name: context.getters.socialSettings.displayName,
        databaseUrl: DATABASE_URL
      });
    },
    // Our feed's id for change notices (Film Club spec 1.3.0 §6.3): random,
    // and new whenever the secret is, so "they hold this id" proves they hold
    // the current feed URL.
    async ensureClubFeedId (context) {
      const secret = context.state.settings?.clubFeedKey;
      if (!secret) return null;
      const existing = currentFeedId(context.state.settings?.clubFeedId, secret);
      if (existing) return existing;
      const id = newFeedId((window.crypto || window.msCrypto).getRandomValues(new Uint8Array(16)));
      await context.dispatch('writeDurably', { path: 'settings/clubFeedId', value: { id, secret } });
      return id;
    },
    // POST our feed (a connect request, §6.2) to someone's inbox, offering
    // change notices. `replyInbox: false` is an answer to a negotiation: it
    // names no inbox, so the two apps can't bounce requests back and forth.
    // Returns our feed id; throws when the inbox refuses.
    async sendOwnFeed (context, { to, replyInbox = true }) {
      const accountKey = context.state.databaseTopKey;
      const secret = context.state.settings?.clubFeedKey;
      const code = context.state.settings?.clubInviteCode;
      // An existing feed needs no republish just to be named.
      const invite = accountKey && secret && code
        ? buildInvite({ accountKey, inviteCode: code, feedUrl: `${DATABASE_URL}/clubFeed/${accountKey}/${secret}.json`, name: context.getters.socialSettings.displayName, databaseUrl: DATABASE_URL })
        : await context.dispatch('createClubInvite');
      if (!invite) return null;
      const feed = await context.dispatch('ensureClubFeedId');
      const post = (withFeed) => fetch(to, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildConnectRequest({
          name: invite.name,
          feedUrl: invite.feedUrl,
          replyInboxUrl: replyInbox ? invite.inboxUrl : null,
          feed: withFeed ? feed : null
        }))
      });
      const response = await post(Boolean(feed));
      if (response.ok) return feed || true;
      // An inbox whose rules predate the negotiation fields still gets the
      // plain request: adding a friend never depends on notices.
      if (feed && replyInbox && (await post(false)).ok) return true;
      throw new Error(`inbox responded ${response.status}`);
    },
    // Field-by-field, and only on a friend who still exists: writing one
    // field of a removed friend would bring back a friend with no feed.
    async updateExternalFriend (context, { id, changes }) {
      if (!id || !context.state.settings?.externalFriends?.[id]) return;
      await Promise.all(Object.entries(changes || {}).map(([field, value]) =>
        context.dispatch('writeDurably', { path: `settings/externalFriends/${id}/${field}`, value })));
    },
    // The inbox, read a page at a time, live while the app is open: connect
    // requests for the Film Club screen, and change notices acted on at once.
    watchClubInbox (context) {
      const me = context.state.databaseTopKey;
      const code = context.state.settings?.clubInviteCode;
      if (!me || !code) return;
      const path = `clubInbox/${me}/${code}`;
      if (clubInboxWatching === path) return;
      clubInboxWatching = path;
      onValue(query(ref(db, path), orderByKey(), limitToFirst(INBOX_PAGE)), (snapshot) => {
        const raw = snapshot.val();
        context.commit('setClubInboxRequests', raw);
        clubInboxProcessing = clubInboxProcessing
          .then(() => context.dispatch('processClubInbox', { raw, path }))
          .catch((error) => console.warn('[film-club] inbox:', error.message));
      }, (error) => {
        clubInboxWatching = null;
        console.warn('[film-club] inbox listener:', error.message);
      });
    },
    // Notices are hints from anyone holding the inbox link (§6.3): one that
    // names no single negotiated friend is deleted unread and costs the
    // friend's database nothing. A real one is persisted as the friend's
    // pending refresh BEFORE it is deleted, so a closed tab loses nothing.
    async processClubInbox (context, { raw, path }) {
      const friends = context.state.settings?.externalFriends || {};
      const plan = planInbox({ raw, friends });
      const feedId = currentFeedId(context.state.settings?.clubFeedId, context.state.settings?.clubFeedKey);
      const remove = (key) => set(ref(db, `${path}/${key}`), null).catch(() => {});

      for (const { key, friendId, request } of plan.negotiations) {
        const normalized = normalizeInboxRequests({ [key]: request })[0];
        if (!normalized) { await remove(key); continue; }
        const { changes, answer, proven } = negotiationUpdate({ friend: friends[friendId], request: normalized, feedId });
        await context.dispatch('updateExternalFriend', { id: friendId, changes });
        // Not provably theirs already: the owner decides, as with any request.
        if (!proven) continue;
        if (answer) {
          try {
            await context.dispatch('sendOwnFeed', { to: answer, replyInbox: false });
            await context.dispatch('updateExternalFriend', { id: friendId, changes: { answeredFeedId: feedId } });
          } catch (error) {
            console.warn('[film-club] could not answer a negotiation:', error.message);
            continue;
          }
        }
        await remove(key);
      }

      const pending = coalesceNotices(plan.notices);
      for (const [friendId, notice] of Object.entries(pending)) {
        const prior = friends[friendId]?.noticePending;
        if (!prior || notice.at >= (Number(prior.at) || 0)) {
          await context.dispatch('updateExternalFriend', { id: friendId, changes: { noticePending: notice } });
        }
      }
      await Promise.all(plan.notices.map(({ key }) => remove(key)));
      Object.keys(pending).forEach((friendId) => context.dispatch('refreshNoticedFriend', friendId));
    },
    // One friend, because a notice said their feed moved. Never counts as the
    // hourly backstop. A notice naming the revision we already hold costs
    // nothing; a failed refresh keeps the pending marker for the next try.
    async refreshNoticedFriend (context, id) {
      if (noticeRefreshes.has(id)) return noticeRefreshes.get(id);
      const run = (async () => {
        const friend = context.state.settings?.externalFriends?.[id];
        const pending = friend?.noticePending;
        if (!pending) return;
        const topKey = context.state.databaseTopKey;
        const cache = topKey ? await loadSnapshot(topKey, `externalSync:${id}`).catch(() => null) : null;
        if (cache?.meta?.revision !== pending.revision) {
          if (!(await syncOneExternalFriend(context, id, friend))) return;
          saveExternalFriendsSnapshot(context);
        }
        if (context.state.settings?.externalFriends?.[id]?.noticePending?.at === pending.at) {
          await context.dispatch('updateExternalFriend', { id, changes: { noticePending: null } });
        }
      })();
      noticeRefreshes.set(id, run);
      try { return await run; } finally { noticeRefreshes.delete(id); }
    },
    // After a publish that moved the feed (and on launch, for retries): one
    // notice per negotiated friend, best effort. One pending revision per
    // friend is kept on the device, newest wins, and retried on later
    // sessions a few times; a notice that never lands is repaired by the
    // reader's hourly backstop. Never throws — a save must not fail on this.
    async sendClubNotices (context, { revision = null } = {}) {
      clubNoticeSending = clubNoticeSending
        .then(() => sendNoticesNow(context, revision))
        .catch((error) => console.warn('[film-club] notices:', error.message));
      return clubNoticeSending;
    },
    // Accepting subscribes to them and, when they told us where to reply,
    // posts our own feed back so they can subscribe to us without a second
    // round of copy-and-paste. Our reply offers change notices; theirs, if it
    // offered them, is bound to the exact feed we just accepted.
    async acceptClubRequest (context, request) {
      if (!request?.feedUrl) return;
      const id = await context.dispatch('addExternalFriend', { name: request.name, feedUrl: request.feedUrl });
      if (request.replyInboxUrl) {
        try {
          const feedId = await context.dispatch('sendOwnFeed', { to: request.replyInboxUrl });
          if (id && typeof feedId === 'string') {
            await context.dispatch('updateExternalFriend', { id, changes: { sentFeedId: feedId, answeredFeedId: feedId, negotiatedInbox: request.replyInboxUrl } });
          }
        } catch (error) {
          console.warn('[film-club] could not reply to invite:', error.message);
        }
      }
      if (id) await context.dispatch('recordFriendNegotiation', { id, request });
      await context.dispatch('dismissClubRequest', request.id);
    },
    async recordFriendNegotiation (context, { id, request }) {
      const friend = context.state.settings?.externalFriends?.[id];
      if (!friend) return;
      const feedId = currentFeedId(context.state.settings?.clubFeedId, context.state.settings?.clubFeedKey);
      // The owner just accepted this request: bind what it carries.
      const { changes } = negotiationUpdate({ friend, request, feedId, explicit: true });
      await context.dispatch('updateExternalFriend', { id, changes });
    },
    async dismissClubRequest (context, requestId) {
      const me = context.state.databaseTopKey;
      const code = context.state.settings?.clubInviteCode;
      if (!me || !code || !requestId) return;
      await set(ref(db, `clubInbox/${me}/${code}/${requestId}`), null);
    },
    // Ask someone on another app to add us, using the invite they sent.
    async sendClubRequest (context, rawInvite) {
      const invite = parseInvite(rawInvite);
      if (!invite) return { ok: false, error: 'That invite could not be read.' };

      const id = await context.dispatch('addExternalFriend', { name: invite.name, feedUrl: invite.feedUrl });

      if (!invite.inboxUrl) {
        return { ok: true, replied: false, note: 'Subscribed. They will need your link to see you.' };
      }
      try {
        const feedId = await context.dispatch('sendOwnFeed', { to: invite.inboxUrl });
        if (!feedId) return { ok: true, replied: false, note: 'Subscribed, but your own feed could not be created.' };
        // Their answer may name no inbox; this is the one it may call back.
        if (id && typeof feedId === 'string') {
          await context.dispatch('updateExternalFriend', { id, changes: { sentFeedId: feedId, negotiatedInbox: invite.inboxUrl } });
        }
        return { ok: true, replied: true };
      } catch (error) {
        return { ok: true, replied: false, note: `Subscribed, but the request didn't send: ${error.message}` };
      }
    },
    async addExternalFriend (context, { name, feedUrl }) {
      const clean = String(feedUrl || '').trim();
      if (!clean || !/^https?:\/\//i.test(clean)) return null;

      // Already in the club? Accepting a connect request and accepting an
      // invite both land here, and each used to mint a fresh id — which is
      // how one friend's feed ended up subscribed twice and every one of
      // his movies appeared twice (report -P-H-twP5kUjwuEanKMm). Re-adding
      // now just refreshes the friend you already have.
      const existingId = findSubscription(context.state.settings?.externalFriends, clean);
      if (existingId) {
        await context.dispatch('syncExternalFriends');
        return existingId;
      }

      const id = `ext-${Date.now().toString(36)}`;
      await context.dispatch('writeDurably', {
        path: `settings/externalFriends/${id}`,
        value: { name: String(name || '').trim() || 'A friend', feedUrl: clean, addedAt: Date.now() }
      });
      await context.dispatch('syncExternalFriends');
      return id;
    },
    async removeExternalFriend (context, id) {
      if (!id) return;
      await context.dispatch('writeDurably', { path: `settings/externalFriends/${id}`, value: null });
      context.commit('setExternalFriendProfile', { id, profile: null });
    },
    // Load whatever club data is still missing — safe to dispatch from
    // anything that renders club data, any number of times. The mount-time
    // fetches (Home, Film Club, Watchlist) read settings/externalFriends and
    // the friend edges, and on a cold start neither has necessarily arrived
    // yet, so a session could run to its end with every pill blank (report
    // -P0EQt6w7iKdJziMXHTi: three Movie Log friends had rated the movie and
    // none showed). FriendsWhoSaw watches club membership and dispatches
    // this as it lands; clubFetchesNeeded (social.js) keeps the re-runs from
    // re-fetching anything already here.
    //
    // `maxAgeMs` (2026-09-06): a surface that is about to SHOW a friend's
    // rating passes how old a copy it will tolerate. Without it only a
    // missing profile is fetched, and an app open since morning shows the
    // morning's snapshot — which is how a friend-log push steered Matt's
    // running app to the film and the pills had nothing to say.
    // One read each of the shared film cache and my synced diary, remembered
    // for the session (re-read after maxAgeMs). Never throws: a screen that
    // asked simply has no Letterboxd data yet.
    async ensureLetterboxdData (context, { maxAgeMs = 10 * 60 * 1000 } = {}) {
      const topKey = context.getters.databaseTopKey;
      if (!topKey || !context.state.isOnline) return;
      if (context.state.letterboxdLoadedAt && Date.now() - context.state.letterboxdLoadedAt < maxAgeMs) return;
      if (letterboxdLoadInFlight) return letterboxdLoadInFlight;
      letterboxdLoadInFlight = (async () => {
        try {
          const [films, reviews] = await Promise.all([
            get(ref(db, 'letterboxdFilms')).then((snapshot) => snapshot.val()),
            get(ref(db, `${topKey}/letterboxd/reviews`)).then((snapshot) => snapshot.val())
          ]);
          context.commit('setLetterboxdData', { films, reviews, at: Date.now() });
        } catch (error) {
          console.warn('Letterboxd data unavailable:', error?.message);
        } finally {
          letterboxdLoadInFlight = null;
        }
      })();
      return letterboxdLoadInFlight;
    },
    ensureClubData (context, { maxAgeMs = Infinity } = {}) {
      context.dispatch('attachSocialListeners');
      const needed = clubFetchesNeeded({
        friendKeys: context.getters.socialFriendKeys,
        nativeProfiles: context.state.socialFriendProfiles,
        externalFriends: context.state.settings?.externalFriends,
        externalProfiles: context.state.externalFriendProfiles,
        fetchedAt: context.state.socialProfilesFetchedAt,
        maxAgeMs
      });
      if (needed.native) context.dispatch('fetchFriendProfiles');
      if (needed.external) context.dispatch('syncExternalFriends');
    },
    // Fetch each subscribed feed that is due and translate it. Failures are
    // per-friend and non-fatal — one unreachable feed must not blank the club.
    async syncExternalFriends (context, { force = false, maxAgeMs = EXTERNAL_FEED_MAX_AGE_MS } = {}) {
      const friends = context.state.settings?.externalFriends || {};
      // Someone else's download quota (see EXTERNAL_FEED_MAX_AGE_MS). Every
      // screen that mounts may ask; most asks are no-ops. A friend who sends
      // change notices is on the hourly backstop instead (friendSyncDue); a
      // pending notice is due at once but never counts as that backstop.
      const feedId = currentFeedId(context.state.settings?.clubFeedId, context.state.settings?.clubFeedKey);
      const now = Date.now();
      const dueFor = (id, friend) => friendSyncDue({
        friend,
        hasProfile: Boolean(context.state.externalFriendProfiles?.[id]),
        syncedAt: context.state.externalFriendSyncedAt?.[id] ?? context.state.externalFriendsSyncedAt,
        maxAgeMs,
        negotiated: isNegotiated(friend, feedId),
        now,
        force
      });
      const due = Object.entries(friends).filter(([id, friend]) => dueFor(id, friend));
      if (!due.length) return;
      const backstop = due.filter(([id, friend]) => dueFor(id, { ...friend, noticePending: null })).map(([id]) => id);
      context.commit('setSocialProfilesFetchedAt', Date.now());
      await Promise.all(due.map(async ([id, friend]) => {
        const ok = await syncOneExternalFriend(context, id, friend);
        const pending = friend.noticePending;
        if (ok && pending && context.state.settings?.externalFriends?.[id]?.noticePending?.at === pending.at) {
          await context.dispatch('updateExternalFriend', { id, changes: { noticePending: null } });
        }
      }));
      const syncedAt = Date.now();
      context.commit('markExternalFriendsSynced', { ids: backstop, at: syncedAt });
      if (backstop.length === Object.keys(friends).length) context.commit('setExternalFriendsSyncedAt', syncedAt);
      // Keep the result for the next launch, so a cold start shows the club
      // at once and asks the feed only for its revision.
      saveExternalFriendsSnapshot(context);
    },

    // ------------------------------------------------------------------
    // Watchlist learning: which recommendation sources actually earn
    // watches. Suggestions are recorded once; when a recorded movie later
    // appears in the library, its source gets the credit. See
    // recommendationStats.js.
    async recordWatchlistSuggestions (context, shownBySource) {
      const pending = context.state.settings?.watchlistLearning?.pending || {};
      const updates = pendingUpdates(pending, shownBySource);
      const entries = Object.entries(updates);
      if (!entries.length) return;

      await Promise.all(entries.map(([tmdbId, record]) =>
        context.dispatch('writeDurably', {
          path: `settings/watchlistLearning/pending/${tmdbId}`,
          value: record
        })
      ));
      // Count what was offered, so a source's hit RATE has a denominator.
      const counts = {};
      entries.forEach(([, record]) => { counts[record.source] = (counts[record.source] || 0) + 1; });
      await Promise.all(Object.entries(counts).map(([source, added]) => {
        const current = Number(context.state.settings?.watchlistLearning?.sources?.[source]?.suggested) || 0;
        return context.dispatch('writeDurably', {
          path: `settings/watchlistLearning/sources/${source}/suggested`,
          value: current + added
        });
      }));
    },
    // Credit sources whose suggestions have since been rated, and forget
    // suggestions that went stale. Safe to run on every watchlist visit.
    async reconcileWatchlistLearning (context, ratedTmdbIds) {
      const learning = context.state.settings?.watchlistLearning;
      if (!learning?.pending) return;
      const { hits, resolved, expired } = reconcilePending(learning.pending, ratedTmdbIds);

      await Promise.all(Object.entries(hits).map(([source, count]) => {
        const current = Number(learning.sources?.[source]?.hits) || 0;
        return context.dispatch('writeDurably', {
          path: `settings/watchlistLearning/sources/${source}/hits`,
          value: current + count
        });
      }));
      await Promise.all([...resolved, ...expired].map((tmdbId) =>
        context.dispatch('writeDurably', {
          path: `settings/watchlistLearning/pending/${tmdbId}`,
          value: null
        })
      ));
    },

    // ------------------------------------------------------------------
    // Magic Mirror feed. The mirror used to read the whole movieLog over
    // unauthenticated REST; the 2026-08-14 lockdown ended that. Rather than
    // reopening the library, publish a few KB of exactly what it renders to
    // mirrorFeed/<userKey>/<secret>, which the rules make world-readable at
    // the secret level only. No credentials live on the mirror.
    // The secret must never live in Cinema Roll's public bundle, so it is
    // generated per-user at runtime and stored with the user's settings.
    async ensureMirrorFeedKey (context) {
      const existing = context.state.settings?.mirrorFeedKey;
      if (typeof existing === 'string' && existing.length >= 16) return existing;
      const bytes = new Uint8Array(16);
      (window.crypto || window.msCrypto).getRandomValues(bytes);
      const key = Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
      await context.dispatch('writeDurably', { path: 'settings/mirrorFeedKey', value: key });
      return key;
    },
    async publishMirrorFeed (context) {
      const me = context.state.databaseTopKey;
      const secret = context.state.settings?.mirrorFeedKey;
      if (!me || !secret || !context.state.dbLoaded) return;
      const entries = context.getters.allMediaAsArray;
      if (!entries.length) return;
      const feed = buildMirrorFeed(entries, getRating);
      await set(ref(db, `mirrorFeed/${me}/${secret}`), feed);
      localStorage.setItem('cinemaRoll.mirrorFeed.lastPublish', String(Date.now()));
    },
    // Bug report, 2026-10-05: Matt rated The Fall and the Magic Mirror kept
    // showing it as Now Showing. The mirror hides a Movie Hat pick once its id
    // is in the feed's ratedIds, but the feed was only republished from Home's
    // six-hourly watcher, so a fresh rating could sit unpublished for hours.
    // Same shape as scheduleSocialPublish: debounced so a run of edits writes
    // the (small) feed once, flushed on pagehide/hidden because a backgrounded
    // PWA doesn't run setTimeout. Home's six-hour watcher stays as a backstop.
    scheduleMirrorPublish (context, { delay = 5000 } = {}) {
      if (!context.state.settings?.mirrorFeedKey) return;
      if (mirrorPublishTimer) clearTimeout(mirrorPublishTimer);
      mirrorPublishTimer = setTimeout(() => {
        mirrorPublishTimer = null;
        context.dispatch('publishMirrorFeed');
      }, delay);
    },
    flushMirrorPublish (context) {
      if (!mirrorPublishTimer) return Promise.resolve();
      clearTimeout(mirrorPublishTimer);
      mirrorPublishTimer = null;
      return context.dispatch('publishMirrorFeed');
    },

    // ------------------------------------------------------------------
    // Social layer. All writes land OUTSIDE the user's private branch, under
    // social/ — see src/assets/javascript/social.js and the database rules.
    // Waits for Firebase to settle the sign-in, like initializeDB, and does
    // nothing without a live session: the router trusts the stored key, so
    // an expired session used to attach these unauthenticated and get refused
    // (Sentry, 2026-10-11). A refused listener never refires, so a refusal
    // clears the marker and the next screen to ask tries again.
    async attachSocialListeners (context) {
      const me = context.getters.socialUserKey;
      if (!me || context.state.socialAttachedFor === me) return;
      context.commit('setSocialAttachedFor', me);
      await authReady;
      if (!auth.currentUser || context.getters.socialUserKey !== me) {
        if (context.state.socialAttachedFor === me) context.commit('setSocialAttachedFor', null);
        return;
      }
      // A warning, not an error: captureConsole files every console.error in
      // Sentry, and a session that expires mid-visit is routine — the router
      // and verifyRestoredSession own the sign-out.
      const cancelled = (label) => (error) => {
        console.warn(`social ${label} listener cancelled:`, error);
        if (context.state.socialAttachedFor === me) context.commit('setSocialAttachedFor', null);
      };
      socialListenerStops = [
        onValue(ref(db, `social/requests/${me}`), (snapshot) => {
          context.commit('setSocialRequests', snapshot.val());
        }, cancelled('requests')),
        onValue(ref(db, 'social/friends'), (snapshot) => {
          context.commit('setSocialEdges', snapshot.val());
          // Profiles feed the Film Club badge on Home, so they load wherever
          // the listeners attach — not just on the Film Club screen.
          context.dispatch('fetchFriendProfiles');
        }, cancelled('friends'))
      ];
    },
    detachSocialListeners (context) {
      socialListenerStops.forEach((stop) => typeof stop === 'function' && stop());
      socialListenerStops = [];
      context.commit('setSocialAttachedFor', null);
    },
    // Publish (or refresh) my public profile + directory row. Safe to call
    // liberally — it no-ops unless social is enabled and the library is in.
    async publishSocialProfile (context) {
      const me = context.getters.socialUserKey;
      const social = context.getters.socialSettings;
      if (!me || isQaAccountKey(me) || !social.enabled || !context.state.settingsLoaded) return;
      const entries = context.getters.allMediaAsArray;
      if (!entries.length) return;
      if (!(await personalAwardsReadyToPublish(context))) return;
      const profile = buildSocialProfile(entries, getRating, {
        name: social.displayName || 'A Cinema Roll user',
        shareRatings: Boolean(social.shareRatings),
        shareCriteria: Boolean(social.shareRatings && social.shareCriteria),
        awards: awardsByMovie(context.state.settings?.personalAwards),
        awardsName: awardNameWithThe(context.state.settings?.personalAwardName)
      });
      await Promise.all([
        set(ref(db, `social/profiles/${me}`), profile),
        set(ref(db, `social/directory/${me}`), { name: profile.name })
      ]);
    },
    // ---- Movie Hat ----------------------------------------------------
    //
    // Movie Hat is a separate app with its own database and stays that way
    // (Matt, 2026-08-16). These actions are the only place Cinema Roll
    // touches it, and they all go through assets/javascript/movieHat.js.

    /**
     * The one-off setup step: read every hat and keep the ones this account
     * is a member of. Deliberately not run on load — it reads the whole
     * database, which is megabytes.
     */
    async findMovieHats (context) {
      // Prefer the Movie Hat account when connected: the hats are keyed by
      // whichever Google address owns them, which needn't be the address you
      // use for Cinema Roll.
      const email = context.state.movieHatEmail || context.state.userEmail;
      if (!email) return [];

      // The index first — the only route that survives the lockdown. The
      // whole-database scan is the fallback for as long as it works, so
      // linking hats keeps working before the index is backfilled.
      let mine;
      try {
        mine = await fetchMyHats(email);
      } catch (error) {
        context.commit('setMovieHatLastFailure', signInFailureSummary(error, 'lookup'));
        throw error;
      }

      if (!mine.length) {
        try {
          mine = hatsForMember(await fetchAllHats(), email);
        } catch {
          // Refused: the database is closed and the index is the only way.
          mine = [];
        }
      }

      context.commit('setAvailableMovieHats', mine);
      return mine;
    },

    /**
     * The one popup — or a minted token, which is the only way an automated
     * browser can get a Movie Hat session at all now the rules are on.
     */
    async connectMovieHat (context, token = null) {
      let user;
      try {
        user = token ? await signIntoMovieHatWithToken(token) : await signIntoMovieHat();
      } catch (error) {
        if (!isSignInDismissal(error)) context.commit('setMovieHatLastFailure', signInFailureSummary(error, 'sign-in'));
        throw error;
      }
      context.commit('setMovieHatUser', user);
      context.commit('setMovieHatLastFailure', null);
      // Whichever account just signed in is the one whose hats to look for.
      // A failure HERE is not a failed sign-in — the account is connected —
      // so it's marked, and the screen says the lookup failed instead.
      if (user?.email) {
        try {
          await context.dispatch('findMovieHats');
        } catch (error) {
          error.movieHatStage = 'lookup';
          throw error;
        }
      }
      return user;
    },

    async disconnectMovieHat (context) {
      await signOutOfMovieHat();
      context.commit('setMovieHatUser', null);
      context.commit('setAvailableMovieHats', []);
    },

    /** Keeps the store in step with the Movie Hat session across reloads. */
    watchMovieHatAuth (context) {
      observeMovieHatAuth((user) => context.commit('setMovieHatUser', user));
    },

    async linkMovieHats (context, hats) {
      await context.dispatch('writeDurably', {
        path: 'settings/movieHat/hats',
        value: (hats || []).map(({ title, dbKey }) => ({ title, dbKey }))
      });
    },

    /**
     * Send one or many movies to a hat. Returns what happened per movie so
     * the screen can say "3 added, 1 was already in there" — Matt asked for
     * exactly that rather than Cinema Roll tracking hat contents itself.
     */
    // `note` is plain-language provenance shown on the hat movie — "not sure
    // you've noticed but there's a way to put a note on any movie you [pull]
    // out of the hat so we have to make sure that's possible from Cinema Roll
    // as well" (2026-08-17). It says WHY the movie went in; `addedBy` already
    // says who put it there.
    async addToMovieHat (context, { title, dbKey = null, entries = [], note = null }) {
      const hat = await fetchHat(title, dbKey);
      if (!hat) throw new Error(`Couldn't find a hat called "${title}"`);

      const addedBy = context.getters.socialSettings?.displayName || context.state.userEmail || 'Cinema Roll';
      const added = [];
      const skipped = [];
      // Tracks within this batch too, so the same movie twice in one list
      // doesn't get added twice.
      const seen = [...hat.movies];

      for (const entry of entries) {
        const payload = toHatMovie(entry, { addedBy, note });
        if (!payload) continue;

        if (alreadyInHat(seen, payload.id)) {
          skipped.push(payload);
          continue;
        }

        await addMovieToHat(hat.title, hat.dbKey, payload);
        seen.push(payload);
        added.push(payload);
      }

      // Stamp it so linkedMovieHats can order by recent use. Derived state,
      // not user-authored, so setDBValue is the right write here.
      //
      // The storage key comes from the RAW settings, never from the getter's
      // index: the getter is now sorted by recency, so its positions stop
      // matching the stored ones the moment any hat is used. Firebase also
      // hands a sparse array back as an object map, so the key can be either.
      if (added.length) {
        const stored = context.state.settings?.movieHat?.hats;
        const key = Array.isArray(stored)
          ? stored.findIndex((linked) => linked?.title === hat.title)
          : Object.keys(stored || {}).find((k) => stored[k]?.title === hat.title);

        if (key !== undefined && key !== -1 && key !== null) {
          context.dispatch('setDBValue', {
            path: `settings/movieHat/hats/${key}/lastUsedAt`,
            value: Date.now()
          });
        }
      }

      return { added, skipped, hat: hat.title };
    },

    /**
     * Which movies are already waiting in any hat you belong to.
     *
     * One request per hat, cached for `maxAgeMs` — a hat's contents change
     * rarely and every read is billed egress on Movie Hat's database, so
     * this deliberately does NOT refetch per screen. Every hat Movie Hat's
     * index lists for you counts, not just the linked ones (bug report,
     * 2026-09-29) — see readHatContents.
     *
     * An incomplete read (a hat, or the index, that couldn't be read) used
     * to be cached for the full ten minutes as if it were the whole answer.
     * Now it's only held for `retryAfterMs`, and it's flagged so the
     * watchlist can say it couldn't check everything. Sending still reports
     * "already in there" either way — that check stays the authority.
     *
     * A complete read is good for six hours (Sentry N+1, reopened
     * 2026-10-11). It is one index read plus one per hat, all at once, and
     * at ten minutes nearly every Film Club or Watchlist open after a break
     * fired the whole burst again. The answer is kept across launches, a
     * draw forces a fresh one, and the send-time check still catches a film
     * someone else added in Movie Hat since.
     */
    ensureMovieHatContents (context, { maxAgeMs = MOVIE_HAT_CONTENTS_MAX_AGE_MS, retryAfterMs = 60 * 1000, force = false } = {}) {
      const hats = context.getters.linkedMovieHats;

      if (!hats.length) {
        context.commit('setMovieHatContents', { ids: {}, at: Date.now(), complete: true });
        return Promise.resolve();
      }

      const { movieHatContentsAt: at, movieHatContentsComplete: complete } = context.state;
      const age = Date.now() - (at || 0);
      if (!force && at && age < (complete === false ? retryAfterMs : maxAgeMs)) return Promise.resolve();

      // A screen full of hat buttons mounts in one tick, and each one asks.
      // One read in flight answers all of them. A forced read (just after a
      // draw) starts its own, since the one in flight may predate the draw.
      if (movieHatContentsInFlight && !force) return movieHatContentsInFlight;

      const read = (async () => {
        const { ids, complete: allRead, accessError, cache } = await readHatContents({
          linked: hats,
          email: context.state.movieHatEmail || context.state.userEmail,
          previousIds: context.state.movieHatMovieIds,
          previous: context.state.movieHatContentsCache
        });
        context.commit('setMovieHatAccessError', accessError);
        const at = Date.now();
        context.commit('setMovieHatContents', { ids, at, complete: allRead, cache });
        const topKey = context.state.databaseTopKey;
        if (topKey) saveSnapshot(topKey, 'movieHatContents', { ids, at, complete: allRead, cache }).catch(() => {});
      })().finally(() => {
        if (movieHatContentsInFlight === read) movieHatContentsInFlight = null;
      });
      movieHatContentsInFlight = read;
      return read;
    },

    /**
     * A card's worth of each linked hat: how many are waiting and what came
     * out last. Two small requests per hat (three when there are new draws),
     * so it is called when the section renders rather than on every page
     * load. The per-hat `records` cache is what keeps them small.
     */
    async loadMovieHatSummaries (context) {
      const hats = context.getters.linkedMovieHats;
      let accessError = null;
      const summaryCache = { ...(context.state.movieHatSummaryCache || {}) };
      const summaries = await Promise.all(hats.map(async (hat) => {
        try {
          // Incremental: a repeat read brings down only the draws made since
          // (Sentry "Large HTTP payload", 2026-10-08 — see fetchHatCard).
          const held = summaryCache[hat.dbKey];
          const card = await fetchHatCard(hat.title, hat.dbKey, held?.records || null);
          if (!card) return { ...hat, error: true };
          summaryCache[hat.dbKey] = { records: card.records };

          // The whole history, not just the newest: "I'd rather just see the
          // whole history from that hat because that's all available"
          // (2026-08-17). Newest first, since that's the reading order —
          // history is stored oldest-first, and the newest draw is the one
          // with the latest dateDrawn rather than simply the last key.
          const history = card.history
            .filter(Boolean)
            .sort((a, b) => (b?.dateDrawn || 0) - (a?.dateDrawn || 0));

          return {
            title: card.title,
            dbKey: card.dbKey,
            waiting: card.waiting,
            lastDrawn: history[0] || null,
            history
          };
        } catch (error) {
          if (isMovieHatAccessError(error)) {
            accessError = accessError || { reason: error.reason, email: error.email };
          }
          return { ...hat, error: true };
        }
      }));

      context.commit('setMovieHatAccessError', accessError);
      context.commit('setMovieHatSummaries', summaries);
      context.commit('setMovieHatSummaryCache', summaryCache);
      const topKey = context.state.databaseTopKey;
      if (topKey) saveSnapshot(topKey, 'movieHatSummaries', summaryCache).catch(() => {});
      return summaries;
    },

    /**
     * Draw, for real: the movie leaves the hat and lands in its history,
     * exactly as it would in Movie Hat itself. Deliberately does NOT lead
     * into rating — "that's dumb. I'll have to watch the movie first."
     */
    async drawFromMovieHat (context, { title, dbKey = null }) {
      const hat = await fetchHat(title, dbKey);
      if (!hat) throw new Error(`Couldn't find a hat called "${title}"`);
      if (!hat.movies.length) return { movie: null, hat: hat.title, remaining: 0 };

      const movie = pickFromHat(hat.movies);
      await commitDraw(hat.title, hat.dbKey, movie);

      return { movie, hat: hat.title, remaining: hat.movies.length - 1 };
    },

    // Republish shortly after the library changes.
    //
    // The only trigger used to be a watcher on Home that published at most
    // once every SIX HOURS, so a friend could rate something and have it not
    // reach anyone until that window rolled over — which is exactly what Matt
    // saw (2026-08-16): "I happen to know that Natalie watched a movie today
    // and rated it, and yet the most recent movie I see is from yesterday."
    //
    // The delay is a coalescing window, not a throttle: rating a batch of ten
    // movies should publish the whole profile once, not ten times, and the
    // profile is a full document (every rated title) rather than a delta.
    // Readers already re-fetch on every visit, so once it is published it is
    // seen immediately.
    // A setTimeout does NOT survive an installed PWA being backgrounded, and
    // that is the normal way this app is left — so the coalescing window above
    // was also a window in which the publish could be lost outright. Seth rated
    // Tenet at 5:21:10pm on 2026-08-30 and closed the app; the push reached
    // Matt's phone immediately, his profile snapshot was never rewritten, and
    // the Film Club (which reads only that snapshot) had no such film for the
    // six hours until Home's backstop came round. A notification for something
    // the club cannot show is worse than a slightly later notification.
    //
    // Two callers, deliberately different:
    //   - publishSocialProfileNow: "this must be visible now" (a new viewing
    //     whose push has already gone out). Always publishes.
    //   - flushSocialPublish: "we're about to be suspended" (pagehide /
    //     visibilitychange). Publishes only if something is actually pending,
    //     so backgrounding the app doesn't write the profile on every tab
    //     switch.
    scheduleSocialPublish (context, { delay = 20000 } = {}) {
      if (!context.getters.socialSettings?.enabled) return;
      if (socialPublishTimer) clearTimeout(socialPublishTimer);
      socialPublishTimer = setTimeout(() => {
        socialPublishTimer = null;
        context.dispatch('publishSocialProfile');
        stampSocialPublish();
      }, delay);
    },
    publishSocialProfileNow (context) {
      if (socialPublishTimer) clearTimeout(socialPublishTimer);
      socialPublishTimer = null;
      // Same guard scheduleSocialPublish uses. Without it a call made while
      // sharing is off would stamp a publish that publishSocialProfile then
      // refuses to make, and the stamp is what Home's backstop trusts.
      if (!context.getters.socialSettings?.enabled) return Promise.resolve();
      stampSocialPublish();
      return context.dispatch('publishSocialProfile');
    },
    flushSocialPublish (context) {
      if (!socialPublishTimer) return Promise.resolve();
      return context.dispatch('publishSocialProfileNow');
    },
    async unpublishSocialProfile (context) {
      const me = context.getters.socialUserKey;
      if (!me) return;
      await Promise.all([
        set(ref(db, `social/profiles/${me}`), null),
        set(ref(db, `social/directory/${me}`), null),
        // The end-of-day copy goes with it (the sweep would also catch it).
        set(ref(db, `social/dayProfiles/${me}`), null).catch(() => {})
      ]);
    },
    async fetchSocialDirectory (context) {
      try {
        const snapshot = await get(ref(db, 'social/directory'));
        context.commit('setSocialDirectory', snapshot.val());
      } catch (error) {
        reportRefusedRead('the Film Club directory', error);
      }
    },
    // ---- Push notifications ------------------------------------------
    //
    // Everything lives under `{topKey}/push/` — the owner's own subtree, so
    // the existing per-account rules already cover it (no rules change, no
    // supervised rules deploy). The Lambda reads it with admin credentials.
    async loadPushState (context) {
      const root = context.getters.databaseTopKey;
      if (!root) return;
      // Runs on every launch (App.vue's initializePush), so a refusal must
      // not escape as an unhandled rejection; state stays as it was.
      try {
        const [prefsSnap, subsSnap] = await Promise.all([
          get(ref(db, `${root}/push/prefs`)),
          get(ref(db, `${root}/push/subscriptions`))
        ]);
        context.commit('setPushPrefs', prefsSnap.val());
        context.commit('setPushSubscribed', subsSnap.exists());
      } catch (error) {
        reportRefusedRead('push settings', error);
      }
    },
    async savePushSubscription (context, record) {
      const root = context.getters.databaseTopKey;
      if (!root || !record?.id) return;
      // update(), not set(): the self-heal refresh on every app open must
      // not clobber createdAt on an existing row.
      const { createdAt, ...refreshable } = record;
      const existing = await get(ref(db, `${root}/push/subscriptions/${record.id}`));
      await update(ref(db, `${root}/push/subscriptions/${record.id}`),
        existing.exists() ? refreshable : record);
      context.commit('setPushSubscribed', true);
      // First subscription also plants full default prefs so the settings
      // UI and the Lambda read the same explicit values.
      if (!context.state.pushPrefs) {
        await context.dispatch('savePushPrefs', pushPrefsWithDefaults(null));
      }
      context.dispatch('publishPushDigest');
    },
    async removePushSubscription (context, id) {
      const root = context.getters.databaseTopKey;
      if (!root || !id) return;
      await set(ref(db, `${root}/push/subscriptions/${id}`), null);
      const remaining = await get(ref(db, `${root}/push/subscriptions`));
      context.commit('setPushSubscribed', remaining.exists());
    },
    async savePushPrefs (context, partial) {
      const root = context.getters.databaseTopKey;
      if (!root) return;
      const merged = { ...pushPrefsWithDefaults(context.state.pushPrefs), ...partial };
      await update(ref(db, `${root}/push/prefs`), merged);
      context.commit('setPushPrefs', merged);
    },
    // Publish the digest the scheduled Lambda reads (see pushDigest.js for
    // the client-computes/server-sends rule). Safe to call liberally: no-ops
    // without a subscribed device or before the library has loaded.
    // --- The weekly newsletter ---------------------------------------------
    //
    // The Lambda composes and writes; this only reads. `current` is the week
    // key of the latest issue, so one read finds it without listing every
    // issue ever written.
    // The Showtimes screen and its Insights card. One read; the sweep
    // rewrites the board every 15 minutes and nothing here edits it.
    async loadTheaterBoard (context) {
      const root = context.getters.databaseTopKey;
      if (!root) return null;
      const [boardSnap, dismissedSnap, remindersSnap] = await Promise.all([
        get(ref(db, `${root}/theaters/board`)),
        get(ref(db, `${root}/theaters/dismissed`)),
        get(ref(db, `${root}/theaters/reminders`))
      ]);
      const board = boardSnap.exists() ? boardSnap.val() : null;
      // Only what is on this board is held in memory. Nothing is DELETED here
      // (2026-10-10: "the showtimes at Udvar Hazy just constantly give me the
      // same movies over and over again") - one empty or failed read put
      // nothing on the board and wiped every dismissal there. The push sweep
      // forgets a dismissal or reminder once the film has been off the board
      // for two weeks (staleTheaterEntries in aws-lambda/pushCadence.js), so
      // a repertory return is still fresh - Matt: "I don't want these to be
      // dismissed for all time. Just for these showings."
      const onBoard = new Set();
      (board?.theaters || []).forEach((t) => (t.listings || []).forEach((l) => onBoard.add(`${t.key}/${l.slug}`)));
      const onScreen = (node) => {
        const kept = {};
        Object.entries(node || {}).forEach(([theaterKey, slugs]) => {
          Object.entries(slugs || {}).forEach(([slug, value]) => {
            if (!board || onBoard.has(`${theaterKey}/${slug}`)) (kept[theaterKey] = kept[theaterKey] || {})[slug] = value;
          });
        });
        return kept;
      };
      context.commit('setTheaterBoard', board);
      context.commit('setTheaterDismissed', onScreen(dismissedSnap.exists() ? dismissedSnap.val() : {}));
      context.commit('setTheaterReminders', onScreen(remindersSnap.exists() ? remindersSnap.val() : {}));
      // A dismissal or reminder the server hasn't got yet (offline, or the
      // page reloaded mid-save) still shows as made.
      await context.dispatch('replayPendingWrites', 'theaters').catch(() => {});
      return board;
    },
    // "a way for me to dismiss things off of this screen … I see that, I'm
    // not more interested or I've already seen it" (2026-09-28). Optimistic:
    // the card goes at once, the write follows.
    // "Swipe left can be remind me again one week before the showtime"
    // (2026-09-28). The app picks the time (src/utils/reminderTime.js); the
    // push sweep sends it and stamps sentAt. `reminder: null` cancels.
    //
    // Both are choices the user made, so both go through writeDurably
    // (2026-10-09: an update reloaded the Showtimes screen mid-dismissing,
    // and a bare update() still on its way would have been lost with the
    // page). loadTheaterBoard replays anything still queued.
    async remindListing (context, { theaterKey, slug, reminder }) {
      const root = context.getters.databaseTopKey;
      if (!root || !theaterKey || !slug) return;
      await context.dispatch('writeDurably', { path: `theaters/reminders/${theaterKey}/${slug}`, value: reminder || null });
    },
    // "A third way that I can mark as that I got tickets" (2026-10-27):
    // `tickets: true` writes { at, tickets: true } instead of a bare time.
    // Everything that skips dismissed films only asks whether the key is
    // there, so the pushes, the badge and the sweep's pruning treat it as a
    // dismissal with no change of their own (showtimesTickets.js).
    async dismissListing (context, { theaterKey, slug, restore = false, tickets = false }) {
      const root = context.getters.databaseTopKey;
      if (!root || !theaterKey || !slug) return;
      // Dealt with here, so its notifications go too (the new-listing one and any reminder).
      if (!restore) closeNotificationsWhere((tag) => tag === `listings-${slug}` || tag.startsWith(`remind-${theaterKey}-${slug}-`));
      const value = restore ? null : (tickets ? ticketsMark(Date.now()) : Date.now());
      await context.dispatch('writeDurably', { path: `theaters/dismissed/${theaterKey}/${slug}`, value });
    },
    // Anyone's theaters (2026-09-30: "other people could set it up for their
    // own local theaters"). The sweep reads theaters/follow; saving asks the
    // push Lambda to build the board at once, so the Showtimes screen isn't
    // empty for up to 15 minutes after setup.
    async loadTheaterFollow (context) {
      const root = context.getters.databaseTopKey;
      if (!root) return null;
      const snap = await get(ref(db, `${root}/theaters/follow`));
      const follow = snap.exists() ? snap.val() : null;
      context.commit('setTheaterFollow', follow);
      return follow;
    },
    async saveTheaterFollow (context, { zip = null, place = null, theaters = [] }) {
      const root = context.getters.databaseTopKey;
      if (!root) return;
      const follow = theaters.length
        ? { zip: zip || null, place: place || null, theaters: theaters.map(({ key, name }) => ({ key, name })), updatedAt: Date.now() }
        : null;
      await set(ref(db, `${root}/theaters/follow`), follow);
      context.commit('setTheaterFollow', follow);
    },
    async loadNewsletter (context) {
      const root = context.getters.databaseTopKey;
      if (!root) return;
      try {
        const [prefsSnap, currentSnap] = await Promise.all([
          get(ref(db, `${root}/newsletter/prefs`)),
          get(ref(db, `${root}/newsletter/current`))
        ]);
        context.commit('setNewsletterPrefs', prefsSnap.val());
        const week = currentSnap.val();
        if (week) {
          const issueSnap = await get(ref(db, `${root}/newsletter/issues/${week}`));
          context.commit('setNewsletterIssue', issueSnap.val());
        } else {
          context.commit('setNewsletterIssue', null);
        }
      } catch (error) {
        // Refused with nobody signed in is the same lapsed session the
        // library listeners report (reportRefusedListener): a warning, or one
        // expired login files a second Sentry issue (2026-10-11).
        if (auth.currentUser) console.error('Failed to load the newsletter:', error);
        else console.warn('Failed to load the newsletter with no sign-in:', error);
      } finally {
        // Always, or a failed read leaves the screen spinning forever.
        context.commit('setNewsletterLoaded', true);
      }
    },
    async saveNewsletterPrefs (context, partial) {
      const root = context.getters.databaseTopKey;
      if (!root) return;
      const merged = { ...(context.state.newsletterPrefs || {}), ...partial };
      await update(ref(db, `${root}/newsletter/prefs`), merged);
      context.commit('setNewsletterPrefs', merged);
      // Opting in with no profile published would give the Lambda nothing to
      // rank against, and the first issue is the one that has to land.
      if (merged.newsletter) await context.dispatch('publishNewsletterProfile');
    },
    // The taste profile the Lambda ranks against. Published from here for the
    // same reason the push digest is: calculatedTotal only exists where
    // getRating runs (see newsletterProfile.js).
    async publishNewsletterProfile (context) {
      const root = context.getters.databaseTopKey;
      if (!root) return;
      if (!context.state.dbLoaded || !context.state.settingsLoaded) return;
      if (!context.state.newsletterPrefs?.newsletter) return;
      try {
        const profile = buildNewsletterProfile({
          entries: context.getters.allMediaAsArray,
          getRating
        });
        await set(ref(db, `${root}/newsletter/profile`), profile);
      } catch (error) {
        console.error('Failed to publish the newsletter profile:', error);
      }
    },
    // devMode only (the screen gates the button). Rebuilds THIS week's issue
    // now rather than waiting for Friday — Matt's testing switch.
    //
    // The endpoint answers 202 and builds off the request: an HTTP API
    // integration times out at 30 seconds and a build is ~30 API round trips
    // plus a frontier-model call (measured: ~52s). So this polls for the new
    // issue rather than waiting on the response, and identifies it by
    // `builtAt` CHANGING — the week key is the same issue being replaced, so
    // its presence proves nothing.
    async rebuildNewsletter (context) {
      await context.dispatch('publishNewsletterProfile');
      const before = context.state.newsletterIssue?.builtAt ?? null;

      const { data } = await postToNewsletter('/newsletter/rebuild', {});
      if (data?.skipped) throw new Error(`Nothing built: ${data.skipped}`);

      for (let tries = 0; tries < NEWSLETTER_REBUILD_TRIES; tries += 1) {
        await new Promise((resolve) => setTimeout(resolve, NEWSLETTER_REBUILD_POLL_MS));
        await context.dispatch('loadNewsletter');
        const now = context.state.newsletterIssue?.builtAt ?? null;
        if (now && now !== before) return context.state.newsletterIssue;
      }
      throw new Error('The issue is still building. Give it a minute and reopen this screen.');
    },
    // The longer list of his people that Newsprinted matches casting news
    // against (newsletterProfile.js buildPeopleList). Not tied to the
    // newsletter switch, and refreshed at most every six hours: names move
    // slowly, and this runs after every library sync.
    async publishPeopleList (context) {
      const root = context.getters.databaseTopKey;
      if (!root || !context.state.dbLoaded || !context.state.settingsLoaded) return;
      if (Date.now() - peoplePublishedAt < PEOPLE_PUBLISH_EVERY_MS) return;
      peoplePublishedAt = Date.now();
      try {
        await set(ref(db, `${root}/newsletter/people`), buildPeopleList({
          entries: context.getters.allMediaAsArray,
          getRating
        }));
      } catch (error) {
        peoplePublishedAt = 0;
        console.error('Failed to publish the people list:', error);
      }
    },
    async publishPushDigest (context) {
      const root = context.getters.databaseTopKey;
      if (!root || !context.state.pushSubscribed) return;
      if (!context.state.dbLoaded || !context.state.settingsLoaded) return;
      const digest = buildPushDigest({
        entries: context.getters.allMediaAsArray,
        settings: context.state.settings || {},
        getRating,
        dismissals: readNoticeDismissals(),
        showtimesSeenAt: context.state.showtimesSeenAt || 0
      });
      try {
        await set(ref(db, `${root}/push/digest`), digest);
      } catch (error) {
        console.error('Failed to publish push digest:', error);
      }
    },
    // The home-screen icon badge: the number of chores waiting (appBadge.js).
    // Set by the app itself, not only by a push, so it's right on any device
    // the moment you leave the app. Zero clears it. The Badging API needs
    // notification permission on iOS; without it this quietly does nothing.
    async refreshAppBadge (context) {
      if (typeof navigator === 'undefined' || !navigator.setAppBadge) return;
      if (!context.state.dbLoaded || !context.state.settingsLoaded) return;
      const digest = buildPushDigest({
        entries: context.getters.allMediaAsArray,
        settings: context.state.settings || {},
        getRating
      });
      // "Showtimes waiting" (off by default): the films still waiting on the
      // Showtimes screen count too. The sweep rewrites the board every 15
      // minutes, so it's re-read at most that often, and only when online.
      const prefs = context.state.pushPrefs || {};
      let films = 0;
      if (prefs.showtimes === true) {
        const stale = Date.now() - (context.state.theaterBoardLoadedAt || 0) > THEATER_BOARD_FRESH_MS;
        if (stale && context.state.isOnline !== false) {
          try { await context.dispatch('loadTheaterBoard'); } catch { /* keep what we have */ }
        }
        films = context.getters.showtimesBadgeCount;
      }
      // Whatever Home has put away for today doesn't count (appBadge.js).
      const now = Date.now();
      const shown = badgeExtrasShown({
        films,
        friendRequests: context.getters.pendingFriendRequests,
        dismissals: readNoticeDismissals(),
        snoozes: readFriendRequestSnoozes(),
        now
      });
      const count = appBadgeCount(digest, prefs, now, shown.films, shown.friendRequests);
      try {
        if (count > 0) await navigator.setAppBadge(count);
        else await navigator.clearAppBadge?.();
      } catch {
        // Permission not granted, or the platform refused — nothing to do.
      }
    },
    async sendFriendRequest (context, toKey) {
      const me = context.getters.socialUserKey;
      if (!me || !toKey || toKey === me) return;
      // Pre-commit my half of the friendship; theirs is created when they
      // accept. One dangling edge grants nothing.
      await set(ref(db, `social/friends/${me}/${toKey}`), true);
      await set(ref(db, `social/requests/${toKey}/${me}`), {
        name: context.getters.socialSettings.displayName || 'A Cinema Roll user',
        at: Date.now()
      });
      // Not awaited: the request is saved; the push is a courtesy.
      announceFriendRequest({ toKey, kind: 'request' });
    },
    async cancelFriendRequest (context, toKey) {
      const me = context.getters.socialUserKey;
      if (!me || !toKey) return;
      await Promise.all([
        set(ref(db, `social/friends/${me}/${toKey}`), null),
        set(ref(db, `social/requests/${toKey}/${me}`), null)
      ]);
    },
    async acceptFriendRequest (context, fromKey) {
      const me = context.getters.socialUserKey;
      if (!me || !fromKey) return;
      await set(ref(db, `social/friends/${me}/${fromKey}`), true);
      await set(ref(db, `social/requests/${me}/${fromKey}`), null);
      // "I should get a notification so I know that they're in my film club
      // now" (2026-10-01). After both writes, so the Lambda sees the edge.
      announceFriendRequest({ toKey: fromKey, kind: 'accepted' });
      context.dispatch('fetchFriendProfiles');
    },
    async declineFriendRequest (context, fromKey) {
      const me = context.getters.socialUserKey;
      if (!me || !fromKey) return;
      await set(ref(db, `social/requests/${me}/${fromKey}`), null);
    },
    // "Right away" or "end of day" for one friend (2026-09-30: "I would
    // rather not see exactly when I watch a movie"). It rides on my own
    // friend edge — `true` or 'day' — which the database rules read to keep
    // an end-of-day friend out of my live profile, and the push Lambda reads
    // to hold their notifications until my midnight. See dayProfileFrom in
    // aws-lambda/pushCadence.js.
    async setFriendTiming (context, { friendKey, timing }) {
      const me = context.getters.socialUserKey;
      if (!me || !friendKey || !context.state.socialEdges?.[me]?.[friendKey]) return;
      await set(ref(db, `social/friends/${me}/${friendKey}`), timing === 'day' ? 'day' : true);
    },
    async removeFriend (context, friendKey) {
      const me = context.getters.socialUserKey;
      if (!me || !friendKey) return;
      await set(ref(db, `social/friends/${me}/${friendKey}`), null);
    },
    /**
     * The awards, current: the offline copy first, then the stamp (a few
     * bytes), then the node only when the stamp moved. Safe to call on every
     * launch and foreground. Before the migration has run the stamp is
     * absent and the settings listener still carries the awards.
     */
    async ensurePersonalAwards (context) {
      // One read at a time: the launch and an early publish ask together.
      if (!personalAwardsInFlight) {
        personalAwardsInFlight = loadPersonalAwards(context)
          .then((ready) => {
            if (ready) context.commit('setPersonalAwardsPending', false);
            return ready;
          })
          .finally(() => { personalAwardsInFlight = null; });
      }
      return personalAwardsInFlight;
    },
    /** A durable write under personalAwards/ plus the stamp that tells other devices. */
    async savePersonalAwards (context, { path, value }) {
      const topKey = context.state.databaseTopKey;
      const now = Date.now();
      await context.dispatch('writeDurably', { path: `personalAwards/${path}`, value });
      await context.dispatch('writeDurably', { path: 'personalAwardsMeta/updatedAt', value: now });
      context.commit('setPersonalAwardsStamp', now);
      if (topKey) saveSnapshot(topKey, 'personalAwards', { data: context.state.personalAwards, updatedAt: now }).catch(() => {});
    },
    /**
     * One-time move of settings/personalAwards to its own node, run by the
     * first launch that sees awards in the settings node. If another device
     * got there first, only the settings copy is dropped.
     */
    async migratePersonalAwards (context, awards) {
      const topKey = context.state.databaseTopKey;
      if (!topKey || !awards || context.state.personalAwardsMigrating) return;
      context.state.personalAwardsMigrating = true;
      try {
        const existing = (await get(ref(db, `${topKey}/personalAwardsMeta/updatedAt`))).val();
        if (!existing) {
          const now = Date.now();
          await update(ref(db), { [`${topKey}/personalAwards`]: awards, [`${topKey}/personalAwardsMeta/updatedAt`]: now });
          context.commit('setPersonalAwards', { data: awards, updatedAt: now });
          saveSnapshot(topKey, 'personalAwards', { data: awards, updatedAt: now }).catch(() => {});
        } else {
          await context.dispatch('ensurePersonalAwards');
        }
        await context.dispatch('writeDurably', { path: 'settings/personalAwards', value: null });
        console.info('[personal-awards] moved out of settings');
      } catch (error) {
        console.warn('personal awards: migration deferred', error?.message);
      } finally {
        context.state.personalAwardsMigrating = false;
      }
    },
    async fetchFriendProfiles (context) {
      const keys = context.getters.socialFriendKeys;
      // Stamped at the START so a second caller in the same moment (the
      // edges listener and a detail page's own ensure, on a cold start) sees
      // a fresh fetch rather than a stale one.
      if (keys.length) context.commit('setSocialProfilesFetchedAt', Date.now());
      const me = context.getters.socialUserKey;
      await Promise.all(keys.map(async (key) => {
        try {
          // A friend who shares with me at the end of the day only lets me
          // read their end-of-day copy; the rules deny the live one.
          const branch = context.state.socialEdges?.[key]?.[me] === 'day' ? 'dayProfiles' : 'profiles';
          // Preflight (audit, 2026-10-06): a profile is up to 250 KB and was
          // re-read whole every refresh. Its `updatedAt` is a few bytes; when
          // it matches the copy in hand, the copy is current.
          const held = context.state.socialFriendProfiles?.[key];
          if (held?.updatedAt) {
            const stamp = (await get(ref(db, `social/${branch}/${key}/updatedAt`))).val();
            if (stamp && stamp === held.updatedAt) return;
          }
          const snapshot = await get(ref(db, `social/${branch}/${key}`));
          context.commit('setSocialFriendProfile', { key, profile: snapshot.val() });
        } catch (error) {
          // A friend who hasn't published yet (or a permission race right
          // after acceptance) is expected — leave their profile absent.
          console.warn('could not fetch friend profile', key, error?.code || error);
        }
      }));
    },
  },
  modules: {
  }
})
